import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Users, CreditCard, Video, Calendar,
  CheckCircle, XCircle, Clock, Eye, Download, RefreshCw,
  ChevronRight, AlertCircle, TrendingUp, FileText, ExternalLink,
  Building2, Mail, Phone, User, Briefcase, Globe, Trash2, Plus, Receipt, Gift, Tag, ToggleLeft, ToggleRight, Wand2,
  FileSpreadsheet, Shield, ClipboardList, MessageSquare, Play, Settings, IndianRupee, Save, Link2, MapPin, Send, Copy,
  UserPlus, Filter, Edit, Loader2, Megaphone
} from 'lucide-react';
import InvoiceManagement from '../components/InvoiceManagement';
import AddonsManager from '../components/AddonsManager';
import PodcastManager from '../components/PodcastManager';
import ActivateProfileModal from '../components/ActivateProfileModal';
import RenewalCallsCard from '../components/RenewalCallsCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab components
const TABS = {
  OVERVIEW: 'overview',
  SUBMISSIONS: 'submissions',
  ALL_VIDEOS: 'all_videos',
  BOOKINGS: 'bookings',
  EVENTS: 'events',
  PROFILES: 'profiles',
  LEADERSHIP: 'leadership',
  TESTIMONIALS: 'testimonials',
  CONNECT: 'connect',
  LEADS: 'leads',
  USERS: 'users',
  PAYMENTS: 'payments',
  TRANSFERS: 'transfers',
  INVOICES: 'invoices',
  COUPONS: 'coupons',
  REFERRALS: 'referrals',
  BROADCASTS: 'broadcasts',
  OPPORTUNITIES: 'opportunities',
  AUDIT: 'audit',
  SETTINGS: 'settings'
};

// Admin Opportunities Tab — lightweight self-contained component
function AdminOpportunitiesTab() {
  const [opps, setOpps] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('all');

  React.useEffect(() => {
    axios.get(`${API_URL}/api/admin/showcase-opportunities`, { withCredentials: true })
      .then(r => setOpps(r.data.opportunities || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/showcase-opportunities/${id}/status`, { status }, { withCredentials: true });
      setOpps(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    } catch { alert('Failed to update'); }
  };

  const filtered = filter === 'all' ? opps : opps.filter(o => o.status === filter);
  const counts = { all: opps.length, draft: opps.filter(o => o.status === 'draft').length, published: opps.filter(o => o.status === 'published').length, closed: opps.filter(o => o.status === 'closed').length };

  if (loading) return <div className="text-zinc-400 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-opportunities-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#C41E3A]" />Showcase Opportunities</h2>
        <div className="flex gap-1">
          {['all', 'draft', 'published', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${filter === f ? 'bg-[#C41E3A] text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-zinc-500 text-center py-8">No opportunities found</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(opp => (
            <div key={opp.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={`admin-opp-${opp.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${opp.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : opp.status === 'closed' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-300'}`}>{opp.status}</span>
                    <span className="text-zinc-500 text-[10px]">{(opp.category || '').replace(/_/g, ' ')}</span>
                  </div>
                  <h3 className="text-white font-semibold text-sm">{opp.title}</h3>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-zinc-500">
                    <span>Profile: <a href={`/p/${opp.profile_slug}`} target="_blank" rel="noopener noreferrer" className="text-[#C41E3A] hover:underline">{opp.profile_name}</a></span>
                    {opp.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.location}</span>}
                    <span>{opp.response_count || 0} responses · {opp.view_count || 0} views</span>
                    <span>Created: {new Date(opp.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {opp.status === 'draft' && (
                    <button onClick={() => changeStatus(opp.id, 'published')} className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded text-xs font-medium transition-colors" data-testid={`publish-opp-${opp.id}`}>
                      Publish
                    </button>
                  )}
                  {opp.status === 'published' && (
                    <>
                      <button onClick={() => changeStatus(opp.id, 'closed')} className="px-2.5 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-xs font-medium transition-colors">Close</button>
                      <button onClick={() => changeStatus(opp.id, 'draft')} className="px-2.5 py-1 bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded text-xs font-medium transition-colors">Unpublish</button>
                    </>
                  )}
                  {opp.status === 'closed' && (
                    <button onClick={() => changeStatus(opp.id, 'published')} className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded text-xs font-medium transition-colors">Reopen</button>
                  )}
                  <a href={`/p/${opp.profile_slug}/opportunity/${opp.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-zinc-700 transition-colors" title="Preview">
                    <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />
                  </a>
                </div>
              </div>
              {/* Inline Responses Preview */}
              {opp.responses && opp.responses.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-2 font-medium">Recent Responses:</p>
                  <div className="space-y-1.5">
                    {opp.responses.slice(0, 3).map((r, i) => (
                      <div key={r.id || i} className="flex items-center justify-between text-xs bg-zinc-800/30 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{r.name}</span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-400">{r.company}</span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-400">{r.email}</span>
                        </div>
                        <span className="text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                    {opp.responses.length > 3 && <p className="text-[10px] text-zinc-600">+{opp.responses.length - 3} more</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// Stats Card Component - defined outside to avoid re-creation on render
const StatCard = ({ title, value, icon: Icon, colorClass = "zinc" }) => {
  const bgColors = {
    zinc: "bg-zinc-500/20",
    amber: "bg-amber-500/20",
    blue: "bg-blue-500/20",
    orange: "bg-orange-500/20",
    green: "bg-green-500/20"
  };
  const textColors = {
    zinc: "text-zinc-500",
    amber: "text-amber-500",
    blue: "text-blue-500",
    orange: "text-orange-500",
    green: "text-green-500"
  };
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${bgColors[colorClass] || bgColors.zinc} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${textColors[colorClass] || textColors.zinc}`} />
        </div>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-zinc-500 text-sm mt-1">{title}</p>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [leadershipProfiles, setLeadershipProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  
  // Video deletion state
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  // Payment confirmation form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'manual',
    reference: ''
  });
  
  // Profile removal modal state
  const [showRemoveProfileModal, setShowRemoveProfileModal] = useState(false);
  const [profileToRemove, setProfileToRemove] = useState(null);
  const [removalReason, setRemovalReason] = useState('quality');
  const [removalNotes, setRemovalNotes] = useState('');
  
  // Users management state
  const [allUsers, setAllUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteNotes, setDeleteNotes] = useState('');
  
  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [referralData, setReferralData] = useState({ referrals: [], stats: {} });
  const [partnersList, setPartnersList] = useState([]);
  const [partnerToggleEmail, setPartnerToggleEmail] = useState('');
  const [referralSettings, setReferralSettings] = useState(null);
  const [editingRates, setEditingRates] = useState(false);
  const [rateForm, setRateForm] = useState(null);
  const [savingRates, setSavingRates] = useState(false);
  const [editingPartnerRates, setEditingPartnerRates] = useState(null);
  const [partnerRateForm, setPartnerRateForm] = useState({ basic_6m: '', basic_1y: '', all_access_1y: '' });
  const [broadcastsList, setBroadcastsList] = useState([]);
  const [broadcastForm, setBroadcastForm] = useState({ subject: '', message: '', audience: 'all' });
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastPreview, setBroadcastPreview] = useState(false);
  const [showCreateCouponModal, setShowCreateCouponModal] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_percent: 100,
    max_uses: '',
    valid_until: '',
    description: '',
    restricted_features: []
  });
  const [availableFeatures] = useState([
    { key: 'translation', name: 'Multi-language Translation' },
    { key: 'showcase', name: 'Carousel + Showcase' },
    { key: 'connect', name: 'BrandStage Connect' },
    { key: 'event_invitations', name: 'Event Invitations' },
    { key: 'testimonials', name: 'Video Testimonials' },
    { key: 'video_recording', name: 'Video Recording (10/mo)' },
  ]);
  
  // Branding modal state
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [brandingSubmission, setBrandingSubmission] = useState(null);
  const [brandingType, setBrandingType] = useState('brandstage');
  const [customIntroFile, setCustomIntroFile] = useState(null);
  const [customOutroFile, setCustomOutroFile] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  
  // Pricing settings state
  const [pricingSettings, setPricingSettings] = useState({
    six_months_price: 4999,
    six_months_price_usd: 59,
    one_year_price: 7999,
    one_year_price_usd: 99,
    connect_price: 2999,
    connect_price_usd: 39,
    all_access_price: 15000,
    all_access_price_usd: 180,
    gst_rate: 18,
    updated_at: null,
    updated_by: null
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(false);
  
  // Connect (Opportunities) state
  const [connectOpportunities, setConnectOpportunities] = useState([]);
  const [connectStats, setConnectStats] = useState(null);
  const [connectFilter, setConnectFilter] = useState('all');
  
  // Payment Requests state
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);
  const [newPaymentRequest, setNewPaymentRequest] = useState({
    user_email: '',
    description: '',
    amount_inr: '',
    amount_usd: '',
    currency_mode: 'both', // 'inr', 'usd', or 'both'
    line_items: [],
    valid_until: '',
    notes: ''
  });
  const [newLineItem, setNewLineItem] = useState({ description: '', amount: '' });
  
  // Profile Transfers state
  const [profileTransfers, setProfileTransfers] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({
    profile_id: '',
    profile_type: 'company',
    new_owner_email: '',
    subscription_handling: 'keep',
    coupon_code: '',
    coupon_discount: 100,
    currency_mode: 'inr' // 'inr' or 'usd' - for payment handovers
  });
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [leadsFilter, setLeadsFilter] = useState('');
  const [editingLead, setEditingLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState('');
  
  // Removal reason options with professional message templates
  const removalReasons = [
    { 
      id: 'quality', 
      label: 'Quality Issues', 
      description: 'Incomplete or unclear content',
      template: `Dear {{contact_name}},

Thank you for creating a profile on BrandStage for {{company_name}}.

After review, we noticed some quality concerns with your profile that may affect how potential partners perceive your business:
- Profile content appears incomplete or unclear
- Some information may need updating or verification

We want your business to make the best impression possible. Your profile has been temporarily removed to give you the opportunity to create a fresh, polished presence.

Your account remains fully active, and you're welcome to create a new profile anytime at brandstage-media.com/profile/create with complete and accurate information.

If you have any questions, please don't hesitate to reach out.

Best regards,
BrandStage Team`
    },
    { 
      id: 'inappropriate', 
      label: 'Inappropriate Content', 
      description: 'Offensive or unsuitable material',
      template: `Dear {{contact_name}},

We're writing regarding your profile for {{company_name}} on BrandStage.

Upon review, we found content that doesn't align with our community guidelines and professional standards. As a platform connecting businesses with potential partners and investors, we maintain strict content policies.

Your profile has been removed, but your account remains active. You're welcome to create a new profile that adheres to our Terms of Service and community guidelines.

For questions about our content policies, please contact us.

Best regards,
BrandStage Team`
    },
    { 
      id: 'spam', 
      label: 'Spam/Fake Profile', 
      description: 'Not a legitimate business',
      template: `Dear User,

The profile for {{company_name}} has been removed from BrandStage.

Our verification process identified concerns about the authenticity of this business profile. BrandStage is committed to maintaining a trusted platform for legitimate business connections.

If you believe this was an error and can provide verification of your business, please contact us with supporting documentation.

Best regards,
BrandStage Team`
    },
    { 
      id: 'violation', 
      label: 'Terms Violation', 
      description: 'Violates terms of service',
      template: `Dear {{contact_name}},

Your profile for {{company_name}} has been removed due to a violation of BrandStage Terms of Service.

We take our community guidelines seriously to ensure a safe and professional environment for all users.

Your account remains active. If you wish to create a new profile, please ensure it complies with our Terms of Service available at brandstage-media.com/terms.

For questions or to discuss this decision, please contact us.

Best regards,
BrandStage Team`
    },
    { 
      id: 'duplicate', 
      label: 'Duplicate Profile', 
      description: 'Same company already has a profile',
      template: `Dear {{contact_name}},

We noticed that {{company_name}} has multiple profiles on BrandStage. To maintain clarity for potential partners and avoid confusion, we've consolidated to a single profile.

If you need to update your company's profile or believe there's been an error, please contact us and we'll be happy to assist.

Best regards,
BrandStage Team`
    },
    { 
      id: 'request', 
      label: 'User Request', 
      description: 'User requested removal',
      template: `Dear {{contact_name}},

As requested, your profile for {{company_name}} has been removed from BrandStage.

Your account remains active, and you're welcome to create a new profile anytime you'd like to showcase your business again.

Thank you for being part of BrandStage. We hope to see you again soon!

Best regards,
BrandStage Team`
    },
    { 
      id: 'other', 
      label: 'Other', 
      description: 'Other reason (specify below)',
      template: `Dear {{contact_name}},

We're writing regarding your profile for {{company_name}} on BrandStage.

After careful review, we've made the decision to remove your profile. 

Your account remains fully active, and you're welcome to create a new profile that meets our platform standards at brandstage-media.com/profile/create.

If you have any questions about this decision, please feel free to reach out.

Best regards,
BrandStage Team`
    }
  ];
  
  // State for user message
  const [userMessage, setUserMessage] = useState('');
  const [activatingProfile, setActivatingProfile] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // First check admin access
      await axios.get(`${API_URL}/api/admin/check`, { withCredentials: true });
      
      const [statsRes, submissionsRes, allSubsRes, bookingsRes, eventsRes, profilesRes, leadershipRes, testimonialsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/submissions/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/submissions/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/booth/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/profiles`, { withCredentials: true }).catch(() => ({ data: { profiles: [] } })),
        axios.get(`${API_URL}/api/admin/leadership-profiles`, { withCredentials: true }).catch(() => ({ data: { profiles: [] } })),
        axios.get(`${API_URL}/api/admin/testimonials`, { withCredentials: true }).catch(() => ({ data: { testimonials: [] } }))
      ]);
      
      setStats(statsRes.data);
      setSubmissions(submissionsRes.data.submissions || []);
      setAllSubmissions(allSubsRes.data.submissions || []);
      setBookings(bookingsRes.data.bookings || []);
      setEvents(eventsRes.data.events || []);
      setProfiles(profilesRes.data.profiles || []);
      setLeadershipProfiles(leadershipRes.data.profiles || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Open removal modal for profile
  const openRemoveProfileModal = (profile) => {
    setProfileToRemove(profile);
    setRemovalReason('quality');
    setRemovalNotes('');
    // Set initial template message with profile data
    const template = removalReasons[0].template
      .replace(/\{\{company_name\}\}/g, profile.company_name || 'your company')
      .replace(/\{\{contact_name\}\}/g, profile.contact_name || 'Valued User');
    setUserMessage(template);
    setShowRemoveProfileModal(true);
  };
  
  // Update message when reason changes
  const handleReasonChange = (reasonId) => {
    setRemovalReason(reasonId);
    const reason = removalReasons.find(r => r.id === reasonId);
    if (reason && profileToRemove) {
      const template = reason.template
        .replace(/\{\{company_name\}\}/g, profileToRemove.company_name || 'your company')
        .replace(/\{\{contact_name\}\}/g, profileToRemove.contact_name || 'Valued User');
      setUserMessage(template);
    }
  };
  
  // Confirm profile removal with reason and send notification
  const confirmRemoveProfile = async () => {
    if (!profileToRemove) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/profiles/${profileToRemove.id}/remove`,
        { 
          reason: removalReason, 
          notes: removalNotes,
          user_message: userMessage,
          send_email: true
        },
        { withCredentials: true }
      );
      setProfiles(profiles.filter(p => p.id !== profileToRemove.id));
      setShowRemoveProfileModal(false);
      setProfileToRemove(null);
      setUserMessage('');
      alert(`Profile removed and notification sent to ${profileToRemove.contact_email}. User account remains active.`);
    } catch (err) {
      console.error('Failed to remove profile:', err);
      alert('Failed to remove profile. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Legacy delete (for user self-deletion)
  const deleteProfile = async (profileId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete the profile for "${companyName}"? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/profiles/${profileId}`, { withCredentials: true });
      setProfiles(profiles.filter(p => p.id !== profileId));
    } catch (err) {
      console.error('Failed to delete profile:', err);
      alert('Failed to delete profile. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch coupons
  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/coupons`, { withCredentials: true });
      setCoupons(response.data.coupons || []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.COUPONS) {
      fetchCoupons();
    }
    if (activeTab === TABS.REFERRALS) {
      axios.get(`${API_URL}/api/admin/referrals`, { withCredentials: true })
        .then(r => setReferralData(r.data))
        .catch(() => {});
      axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true })
        .then(r => setPartnersList(r.data.partners || []))
        .catch(() => {});
      axios.get(`${API_URL}/api/admin/referral-settings`, { withCredentials: true })
        .then(r => { setReferralSettings(r.data); setRateForm(r.data); })
        .catch(() => {});
    }
    if (activeTab === TABS.BROADCASTS) {
      axios.get(`${API_URL}/api/admin/broadcasts`, { withCredentials: true })
        .then(r => setBroadcastsList(r.data.broadcasts || []))
        .catch(() => {});
    }
  }, [activeTab]);

  // Fetch users for Users tab
  const fetchUsers = async (page = 0, search = '') => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users?skip=${page * 50}&limit=50&search=${encodeURIComponent(search)}`, { withCredentials: true });
      setAllUsers(response.data.users || []);
      setUsersTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === TABS.USERS) {
      fetchUsers(usersPage, usersSearch);
    }
  }, [activeTab, usersPage, usersSearch]);
  
  // Fetch pricing settings
  const fetchPricingSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings/pricing`, { withCredentials: true });
      setPricingSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch pricing settings:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.SETTINGS) {
      fetchPricingSettings();
    }
  }, [activeTab]);
  
  // Fetch Connect opportunities
  const fetchConnectData = async () => {
    try {
      const [oppsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/connect/opportunities?status=${connectFilter === 'all' ? '' : connectFilter}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/connect/stats`, { withCredentials: true })
      ]);
      setConnectOpportunities(oppsRes.data.opportunities || []);
      setConnectStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch Connect data:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.CONNECT) {
      fetchConnectData();
    }
  }, [activeTab, connectFilter]);
  
  // Connect actions
  const approveOpportunity = async (oppId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/connect/opportunities/${oppId}/approve`, {}, { withCredentials: true });
      fetchConnectData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };
  
  const rejectOpportunity = async (oppId) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/connect/opportunities/${oppId}/reject?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      fetchConnectData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };
  
  const closeOpportunity = async (oppId) => {
    if (!window.confirm('Close this opportunity?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/connect/opportunities/${oppId}/close`, {}, { withCredentials: true });
      fetchConnectData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to close');
    } finally {
      setActionLoading(false);
    }
  };
  
  const routeOpportunity = async (oppId, routeTo) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/connect/opportunities/${oppId}/route?route_to=${routeTo}`, {}, { withCredentials: true });
      alert(`Routed to ${routeTo.toUpperCase()}`);
      fetchConnectData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to route');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Fetch Payment Requests
  const fetchPaymentRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-requests`, { withCredentials: true });
      setPaymentRequests(response.data.payment_requests || []);
    } catch (err) {
      console.error('Failed to fetch payment requests:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.PAYMENTS) {
      fetchPaymentRequests();
    }
  }, [activeTab]);
  
  // Create Payment Request
  const createPaymentRequest = async () => {
    // Validate based on currency mode
    const needsINR = newPaymentRequest.currency_mode === 'inr' || newPaymentRequest.currency_mode === 'both';
    const needsUSD = newPaymentRequest.currency_mode === 'usd';
    
    if (!newPaymentRequest.user_email || !newPaymentRequest.description) {
      alert('Please fill in email and description');
      return;
    }
    
    if (needsINR && !newPaymentRequest.amount_inr) {
      alert('Please enter INR amount');
      return;
    }
    
    if (needsUSD && !newPaymentRequest.amount_usd) {
      alert('Please enter USD amount');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/payment-requests`, {
        user_email: newPaymentRequest.user_email,
        description: newPaymentRequest.description,
        amount_inr: newPaymentRequest.amount_inr ? parseInt(newPaymentRequest.amount_inr) : (newPaymentRequest.amount_usd ? Math.round(parseInt(newPaymentRequest.amount_usd) * 83) : 0),
        amount_usd: newPaymentRequest.amount_usd ? parseInt(newPaymentRequest.amount_usd) : null,
        currency_mode: newPaymentRequest.currency_mode,
        line_items: newPaymentRequest.line_items,
        valid_until: newPaymentRequest.valid_until || null,
        notes: newPaymentRequest.notes || ''
      }, { withCredentials: true });
      
      setShowCreatePaymentModal(false);
      setNewPaymentRequest({
        user_email: '',
        description: '',
        amount_inr: '',
        amount_usd: '',
        currency_mode: 'both',
        line_items: [],
        valid_until: '',
        notes: ''
      });
      fetchPaymentRequests();
      alert('Payment request sent successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create payment request');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Add line item to payment request
  const addLineItem = () => {
    if (!newLineItem.description || !newLineItem.amount) return;
    setNewPaymentRequest({
      ...newPaymentRequest,
      line_items: [...newPaymentRequest.line_items, {
        description: newLineItem.description,
        amount: parseInt(newLineItem.amount)
      }]
    });
    setNewLineItem({ description: '', amount: '' });
  };
  
  // Remove line item
  const removeLineItem = (index) => {
    setNewPaymentRequest({
      ...newPaymentRequest,
      line_items: newPaymentRequest.line_items.filter((_, i) => i !== index)
    });
  };
  
  // Fetch Profile Transfers
  const fetchProfileTransfers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/profile-transfers`, { withCredentials: true });
      setProfileTransfers(response.data.transfers || []);
    } catch (err) {
      console.error('Failed to fetch profile transfers:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.TRANSFERS) {
      fetchProfileTransfers();
    }
  }, [activeTab]);
  
  // Initiate Profile Transfer
  const initiateTransfer = async () => {
    if (!transferForm.profile_id || !transferForm.new_owner_email) {
      alert('Please select a profile and enter the new owner email');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/profiles/transfer`, {
        profile_id: transferForm.profile_id,
        profile_type: transferForm.profile_type,
        new_owner_email: transferForm.new_owner_email,
        subscription_handling: transferForm.subscription_handling,
        coupon_code: transferForm.subscription_handling === 'coupon' ? transferForm.coupon_code : null,
        coupon_discount: transferForm.subscription_handling === 'coupon' ? parseInt(transferForm.coupon_discount) : null
      }, { withCredentials: true });
      
      setShowTransferModal(false);
      setTransferForm({
        profile_id: '',
        profile_type: 'company',
        new_owner_email: '',
        subscription_handling: 'keep',
        coupon_code: '',
        coupon_discount: 100,
        currency_mode: 'inr'
      });
      fetchProfileTransfers();
      
      // Show claim URL
      alert(`Transfer initiated!\n\nClaim URL: ${response.data.claim_url}\n\nAn email has been sent to the new owner.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to initiate transfer');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Fetch Leads
  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (leadsFilter) params.append('status', leadsFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/leads?${params.toString()}`, { withCredentials: true });
      setLeads(response.data.leads || []);
      setLeadsStats(response.data.stats || { total: 0, new: 0, contacted: 0, converted: 0 });
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    }
  };
  
  useEffect(() => {
    if (activeTab === TABS.LEADS) {
      fetchLeads();
    }
  }, [activeTab, leadsFilter]);
  
  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/api/admin/leads/${leadId}`, {
        status: newStatus,
        notes: leadNotes || undefined
      }, { withCredentials: true });
      
      setEditingLead(null);
      setLeadNotes('');
      fetchLeads();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update lead');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Download leads CSV
  const downloadLeadsCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (leadsFilter) params.append('status', leadsFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/leads/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `brandstage_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download leads');
    }
  };
  
  // Save pricing settings
  const savePricingSettings = async () => {
    setPricingLoading(true);
    setPricingSaved(false);
    try {
      await axios.put(`${API_URL}/api/admin/settings/pricing`, {
        six_months_price: parseInt(pricingSettings.six_months_price) || 4999,
        six_months_price_usd: parseInt(pricingSettings.six_months_price_usd) || 59,
        one_year_price: parseInt(pricingSettings.one_year_price) || 7999,
        one_year_price_usd: parseInt(pricingSettings.one_year_price_usd) || 99,
        connect_price: parseInt(pricingSettings.connect_price) || 2999,
        connect_price_usd: parseInt(pricingSettings.connect_price_usd) || 39,
        all_access_price: parseInt(pricingSettings.all_access_price) || 15000,
        all_access_price_usd: parseInt(pricingSettings.all_access_price_usd) || 180,
        gst_rate: parseInt(pricingSettings.gst_rate) || 18
      }, { withCredentials: true });
      setPricingSaved(true);
      setTimeout(() => setPricingSaved(false), 3000);
      fetchPricingSettings();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setPricingLoading(false);
    }
  };
  
  // Create coupon
  const createCoupon = async () => {
    if (!newCoupon.code.trim()) {
      alert('Please enter a coupon code');
      return;
    }
    
    try {
      setActionLoading(true);
      await axios.post(`${API_URL}/api/admin/coupons`, {
        code: newCoupon.code.toUpperCase(),
        discount_percent: parseInt(newCoupon.discount_percent) || 100,
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
        valid_until: newCoupon.valid_until || null,
        description: newCoupon.description || '',
        restricted_features: newCoupon.restricted_features.length > 0 ? newCoupon.restricted_features : null
      }, { withCredentials: true });
      
      setShowCreateCouponModal(false);
      setNewCoupon({ code: '', discount_percent: 100, max_uses: '', valid_until: '', description: '', restricted_features: [] });
      fetchCoupons();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Admin delete user
  const handleDeleteUser = async () => {
    if (!userToDelete || !deleteReason) return;
    if (!window.confirm(`Permanently delete ${userToDelete.email}? This cannot be undone.`)) return;
    try {
      setActionLoading(true);
      await axios.post(`${API_URL}/api/admin/users/${userToDelete.id}/delete`, {
        reason: deleteReason,
        notes: deleteNotes
      }, { withCredentials: true });
      alert(`Account ${userToDelete.email} has been deleted. Notification email sent.`);
      setShowDeleteUserModal(false);
      setUserToDelete(null);
      setDeleteReason('');
      setDeleteNotes('');
      fetchUsers(usersPage, usersSearch);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };


  // Toggle coupon active/inactive
  const toggleCoupon = async (couponId) => {
    try {
      const coupon = coupons.find(c => c.id === couponId);
      await axios.put(`${API_URL}/api/admin/coupons/${couponId}`, {
        is_active: !coupon.is_active
      }, { withCredentials: true });
      fetchCoupons();
    } catch (err) {
      alert('Failed to toggle coupon');
    }
  };
  
  // Delete coupon
  const deleteCoupon = async (couponId, code) => {
    if (!window.confirm(`Deactivate coupon "${code}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/coupons/${couponId}`, { withCredentials: true });
      fetchCoupons();
    } catch (err) {
      alert('Failed to deactivate coupon');
    }
  };

  // Delete single submission
  const deleteSubmission = async (submissionId, name) => {
    if (!window.confirm(`Delete submission from "${name}"? This cannot be undone.`)) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/submissions/${submissionId}`, { withCredentials: true });
      setAllSubmissions(prev => prev.filter(s => (s._id || s.id) !== submissionId));
      setSubmissions(prev => prev.filter(s => (s._id || s.id) !== submissionId));
      setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Bulk delete submissions
  const bulkDeleteSubmissions = async () => {
    if (selectedSubmissions.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedSubmissions.length} selected submissions? This cannot be undone.`)) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/submissions/bulk-delete`, 
        { submission_ids: selectedSubmissions },
        { withCredentials: true }
      );
      setAllSubmissions(prev => prev.filter(s => !selectedSubmissions.includes(s._id || s.id)));
      setSubmissions(prev => prev.filter(s => !selectedSubmissions.includes(s._id || s.id)));
      setSelectedSubmissions([]);
      alert(`Deleted ${selectedSubmissions.length} submissions`);
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Approve submission
  const handleApproveSubmission = async (submissionId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/submissions/${submissionId}/approve`, {}, { withCredentials: true });
      setSubmissions(prev => prev.filter(s => s._id !== submissionId && s.id !== submissionId));
      fetchData();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Reject submission
  const handleRejectSubmission = async (submissionId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('reason', reason);
      await axios.post(`${API_URL}/api/admin/submissions/${submissionId}/reject`, formData, { withCredentials: true });
      setSubmissions(prev => prev.filter(s => s._id !== submissionId && s.id !== submissionId));
      fetchData();
    } catch (err) {
      alert('Failed to reject: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Open branding modal
  const openBrandingModal = (submission) => {
    setBrandingSubmission(submission);
    setBrandingType('brandstage');
    setCustomIntroFile(null);
    setCustomOutroFile(null);
    setShowBrandingModal(true);
  };

  // Submit branding request
  const handleSubmitBranding = async () => {
    if (!brandingSubmission) return;
    
    if (brandingType === 'custom' && !customIntroFile && !customOutroFile) {
      alert('Please upload at least one file (intro or outro) for custom branding');
      return;
    }
    
    setBrandingLoading(true);
    try {
      const formData = new FormData();
      formData.append('branding_type', brandingType);
      
      if (brandingType === 'custom') {
        if (customIntroFile) {
          formData.append('custom_intro', customIntroFile);
        }
        if (customOutroFile) {
          formData.append('custom_outro', customOutroFile);
        }
      }
      
      await axios.post(
        `${API_URL}/api/admin/submissions/${brandingSubmission._id || brandingSubmission.id}/add-branding`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const brandingLabel = brandingType === 'custom' ? 'Custom' : 'BrandStage';
      alert(`${brandingLabel} branding process started! The video will be processed and sent to the user.`);
      setShowBrandingModal(false);
      setBrandingSubmission(null);
      fetchData();
    } catch (err) {
      alert('Failed to start branding: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBrandingLoading(false);
    }
  };

  // Reset stuck branding
  const handleResetBranding = async (submissionId, name) => {
    if (!window.confirm(`Reset branding status for "${name}"'s video? This will allow you to retry branding.`)) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/submissions/${submissionId}/reset-branding`, {}, { withCredentials: true });
      alert('Branding status reset. You can now retry branding.');
      fetchData();
    } catch (err) {
      alert('Failed to reset: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Confirm payment
  const handleConfirmPayment = async (bookingId) => {
    if (!paymentForm.amount) {
      alert('Please enter payment amount');
      return;
    }
    
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('amount', paymentForm.amount);
      formData.append('method', paymentForm.method);
      formData.append('reference', paymentForm.reference);
      
      const res = await axios.post(`${API_URL}/api/admin/bookings/${bookingId}/confirm-payment`, formData, { withCredentials: true });
      alert(`Payment confirmed! Event code: ${res.data.event_code}`);
      setSelectedItem(null);
      setPaymentForm({ amount: '', method: 'manual', reference: '' });
      fetchData();
    } catch (err) {
      alert('Failed to confirm payment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }

  // Access denied screen
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-zinc-400 mb-6">
            You do not have permission to access the admin dashboard. 
            This area is restricted to authorized BrandStage administrators only.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-bold transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-20">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-6 h-6 text-[#C41E3A]" />
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
            </div>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 flex-wrap">
            {[
              { id: TABS.OVERVIEW, label: 'Overview', icon: TrendingUp },
              { id: TABS.SUBMISSIONS, label: `Reviews (${submissions.length})`, icon: Clock },
              { id: TABS.ALL_VIDEOS, label: `All Videos (${allSubmissions.length})`, icon: Video },
              { id: TABS.PROFILES, label: `Profiles (${profiles.length})`, icon: Building2 },
              { id: TABS.LEADERSHIP, label: `Leaders (${leadershipProfiles.length})`, icon: User },
              { id: TABS.TESTIMONIALS, label: `Testimonials (${testimonials.filter(t => t.status === 'pending_review').length})`, icon: MessageSquare },
              { id: TABS.CONNECT, label: 'Connect', icon: Link2 },
              { id: TABS.LEADS, label: `Leads (${leadsStats.new})`, icon: UserPlus },
              { id: TABS.USERS, label: `Users (${usersTotal})`, icon: Users },
              { id: TABS.PAYMENTS, label: 'Payments', icon: IndianRupee },
              { id: TABS.TRANSFERS, label: 'Transfers', icon: Send },
              { id: TABS.INVOICES, label: 'Invoices', icon: Receipt },
              { id: TABS.COUPONS, label: 'Coupons', icon: Gift },
              { id: TABS.REFERRALS, label: 'Referrals', icon: Users },
              { id: TABS.BROADCASTS, label: 'Broadcasts', icon: Mail },
              { id: TABS.OPPORTUNITIES, label: 'Opportunities', icon: Megaphone },
              { id: TABS.AUDIT, label: 'Audit & Export', icon: Shield },
              { id: TABS.SETTINGS, label: 'Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === TABS.OVERVIEW && stats && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title="Pending Reviews" value={stats.submissions?.pending_review || 0} icon={Clock} colorClass="amber" />
              <StatCard title="Today's Submissions" value={stats.submissions?.today || 0} icon={Video} colorClass="blue" />
              <StatCard title="Pending Payments" value={stats.bookings?.pending_payment || 0} icon={CreditCard} colorClass="orange" />
              <StatCard title="Total Revenue" value={`₹${(stats.bookings?.revenue || 0).toLocaleString()}`} icon={TrendingUp} colorClass="green" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard title="Total Submissions" value={stats.submissions?.total || 0} icon={Video} />
              <StatCard title="Active Bookings" value={stats.bookings?.active || 0} icon={CheckCircle} colorClass="green" />
              <StatCard title="Active Events" value={stats.events?.active || 0} icon={Calendar} />
            </div>
            <RenewalCallsCard />
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === TABS.SUBMISSIONS && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Pending Video Reviews</h2>
            
            {submissions.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-zinc-400">No pending reviews</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map(sub => (
                  <div key={sub._id || sub.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Video preview */}
                      <div className="lg:w-64 flex-shrink-0">
                        {sub.cloudinary_url && (
                          <video
                            src={sub.cloudinary_url}
                            controls
                            className="w-full rounded-lg bg-black"
                          />
                        )}
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold">{sub.name}</h3>
                            <p className="text-zinc-400">{sub.company}</p>
                          </div>
                          <span className="text-xs text-zinc-500">{new Date(sub.created_at).toLocaleString()}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-zinc-500">Designation</p>
                            <p>{sub.designation || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Email</p>
                            <p>{sub.email}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Mobile</p>
                            <p>{sub.mobile || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Purpose</p>
                            <p>{sub.purpose?.replace('_', ' ') || 'N/A'}</p>
                          </div>
                        </div>
                        
                        <p className="text-zinc-400 text-sm italic mb-4">&quot;{sub.prompt}&quot;</p>
                        
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => handleApproveSubmission(sub._id || sub.id)}
                            disabled={actionLoading}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve & Brand
                          </button>
                          <button
                            onClick={() => handleRejectSubmission(sub._id || sub.id)}
                            disabled={actionLoading}
                            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </button>
                          <a
                            href={sub.cloudinary_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Raw Video
                          </a>
                          {sub.branded_url && (
                            <a
                              href={sub.branded_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Branded Video
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Videos Tab - For custom editing work */}
        {activeTab === TABS.ALL_VIDEOS && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">All Video Submissions</h2>
              <div className="flex items-center gap-4">
                {selectedSubmissions.length > 0 && (
                  <button
                    onClick={bulkDeleteSubmissions}
                    disabled={actionLoading}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedSubmissions.length})
                  </button>
                )}
                <p className="text-zinc-500 text-sm">Download raw videos for custom editing</p>
              </div>
            </div>
            
            {allSubmissions.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Video className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No submissions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-left">
                      <th className="px-4 py-3 text-zinc-500">
                        <input 
                          type="checkbox"
                          checked={selectedSubmissions.length === allSubmissions.length && allSubmissions.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSubmissions(allSubmissions.map(s => s._id || s.id));
                            } else {
                              setSelectedSubmissions([]);
                            }
                          }}
                          className="rounded border-zinc-600 bg-zinc-800"
                        />
                      </th>
                      <th className="px-4 py-3 text-zinc-500">Date</th>
                      <th className="px-4 py-3 text-zinc-500">Name</th>
                      <th className="px-4 py-3 text-zinc-500">Company</th>
                      <th className="px-4 py-3 text-zinc-500">Event</th>
                      <th className="px-4 py-3 text-zinc-500">Status</th>
                      <th className="px-4 py-3 text-zinc-500">Downloads</th>
                      <th className="px-4 py-3 text-zinc-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSubmissions.map(sub => (
                      <tr key={sub._id || sub.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="px-4 py-3">
                          <input 
                            type="checkbox"
                            checked={selectedSubmissions.includes(sub._id || sub.id)}
                            onChange={(e) => {
                              const id = sub._id || sub.id;
                              if (e.target.checked) {
                                setSelectedSubmissions([...selectedSubmissions, id]);
                              } else {
                                setSelectedSubmissions(selectedSubmissions.filter(s => s !== id));
                              }
                            }}
                            className="rounded border-zinc-600 bg-zinc-800"
                          />
                        </td>
                        <td className="px-4 py-3 text-zinc-400">
                          {new Date(sub.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{sub.name}</p>
                            <p className="text-zinc-500 text-xs">{sub.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">{sub.company}</td>
                        <td className="px-4 py-3 text-zinc-400">
                          <div className="flex items-center gap-1.5">
                            {sub.source === 'quickbite' ? (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/20 text-purple-400 font-bold uppercase">Quick Bite</span>
                            ) : (
                              <span>{sub.event_name || 'Free'}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            (sub.branding_status || sub.status) === 'complete' ? 'bg-green-500/20 text-green-400' :
                            (sub.branding_status || sub.status) === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                            (sub.branding_status || sub.status) === 'pending_review' ? 'bg-amber-500/20 text-amber-400' :
                            sub.editing_requested ? 'bg-purple-500/20 text-purple-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {sub.editing_requested && sub.status !== 'complete' ? 'editing_requested' : (sub.branding_status || sub.status || 'pending')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {sub.cloudinary_url && (
                              <a
                                href={sub.cloudinary_url.replace('/upload/', '/upload/fl_attachment/')}
                                className="text-zinc-400 hover:text-white text-xs underline"
                              >
                                Raw
                              </a>
                            )}
                            {sub.video_url && !sub.cloudinary_url && (
                              <a
                                href={sub.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-400 hover:text-white text-xs underline"
                              >
                                Raw
                              </a>
                            )}
                            {sub.branded_url && (
                              <a
                                href={sub.branded_url.replace('/upload/', '/upload/fl_attachment/')}
                                className="text-[#C41E3A] hover:text-white text-xs underline"
                              >
                                Branded
                              </a>
                            )}
                            {sub.share_url && (
                              <a
                                href={sub.share_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 hover:text-white text-xs underline"
                              >
                                Share
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {/* Add Branding Button - show if not complete or if failed */}
                            {sub.cloudinary_url && (!sub.branded_url || sub.branding_status === 'failed' || sub.branding_status === 'error') && (
                              <button
                                onClick={() => openBrandingModal(sub)}
                                disabled={actionLoading || sub.branding_status === 'processing'}
                                className={`p-1.5 rounded transition-colors ${
                                  sub.branding_status === 'processing' 
                                    ? 'bg-blue-500/20 text-blue-400 cursor-wait' 
                                    : 'bg-[#C41E3A]/20 text-[#C41E3A] hover:bg-[#C41E3A]/30'
                                }`}
                                title={sub.branding_status === 'processing' ? 'Branding in progress...' : 'Add Branding'}
                                data-testid={`add-branding-btn-${sub._id || sub.id}`}
                              >
                                <Wand2 className="w-4 h-4" />
                              </button>
                            )}
                            {/* Reset button for stuck processing */}
                            {sub.branding_status === 'processing' && (
                              <button
                                onClick={() => handleResetBranding(sub._id || sub.id, sub.name)}
                                disabled={actionLoading}
                                className="p-1.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                                title="Reset stuck branding"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteSubmission(sub._id || sub.id, sub.name)}
                              className="text-red-400 hover:text-red-300 p-1"
                              title="Delete submission"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === TABS.BOOKINGS && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Booth Bookings</h2>
            
            {bookings.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No bookings yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <div key={booking.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{booking.customer?.company}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            booking.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            booking.status === 'pending_payment' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {booking.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-zinc-500">Contact</p>
                            <p>{booking.customer?.name}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Package</p>
                            <p>{booking.package_type?.replace('_', ' ')} - ₹{booking.package_price?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Event</p>
                            <p>{booking.event_name}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Dates</p>
                            <p>{booking.event_start_date} to {booking.event_end_date}</p>
                          </div>
                        </div>
                        
                        {booking.event_code && (
                          <div className="mt-3 p-2 bg-zinc-800 rounded inline-block">
                            <span className="text-zinc-400 text-sm mr-2">Code:</span>
                            <span className="font-mono font-bold text-[#C41E3A]">{booking.event_code}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {booking.status === 'pending_payment' && (
                          <button
                            onClick={() => {
                              setSelectedItem(booking);
                              setPaymentForm({ amount: booking.package_price || '', method: 'manual', reference: '' });
                            }}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            <CreditCard className="w-4 h-4" />
                            Confirm Payment
                          </button>
                        )}
                        <a
                          href={`${API_URL}/api/admin/bookings/${booking.id}/record`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <FileText className="w-4 h-4" />
                          View Record
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === TABS.EVENTS && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold mb-4">Event Access Codes</h2>
            
            {events.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No events yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map(event => (
                  <div key={event.id || event._id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold">{event.name}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        event.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="bg-zinc-800 rounded-lg p-4 text-center mb-4">
                      <p className="text-zinc-500 text-xs mb-1">Access Code</p>
                      <p className="font-mono text-2xl font-bold text-[#C41E3A]">{event.access_code}</p>
                    </div>
                    
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Dates</span>
                        <span>{event.start_date?.split('T')[0]} - {event.end_date?.split('T')[0]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Daily Limit</span>
                        <span>{event.daily_limit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Total Recordings</span>
                        <span>{event.total_recordings || 0}</span>
                      </div>
                      {event.company && (
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Company</span>
                          <span>{event.company}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROFILES TAB */}
        {activeTab === TABS.PROFILES && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Company Profiles</h2>
              <Link
                to="/profile/create"
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Profile
              </Link>
            </div>
            
            {profiles.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No profiles created yet</p>
                <Link
                  to="/profile/create"
                  className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Your First Profile
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {profiles.map(profile => (
                  <div key={profile.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-start justify-between gap-4">
                      {/* Profile Info */}
                      <div className="flex items-start gap-4 flex-1">
                        {profile.logo_url ? (
                          <img src={profile.logo_url} alt={profile.company_name} className="w-16 h-16 object-contain bg-white rounded-lg p-1" />
                        ) : (
                          <div className="w-16 h-16 bg-zinc-700 rounded-lg flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-zinc-500" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{profile.company_name}</h3>
                          {profile.tagline && <p className="text-zinc-400 text-sm">{profile.tagline}</p>}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-zinc-500">
                            {profile.headquarters && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {profile.headquarters}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {profile.views || 0} views
                            </span>
                            {profile.created_on_behalf_of && (
                              <span className="bg-[#1E3A5F]/30 text-[#6B9FD4] px-2 py-0.5 rounded text-xs">
                                For: {profile.created_on_behalf_of}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <a
                          href={`/p/${profile.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </a>
                        <a
                          href={`/profile/edit/${profile.id}`}
                          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </a>
                        <button
                          onClick={() => setActivatingProfile(profile)}
                          className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                          data-testid={`admin-activate-profile-${profile.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {profile.subscription_status === 'active' ? `Active · ${profile.plan_tier === 'basic' ? 'Basic' : 'All-Access'}` : 'Activate'}
                        </button>
                        <button
                          onClick={() => openRemoveProfileModal(profile)}
                          disabled={actionLoading}
                          className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    {/* Additional Info */}
                    <div className="mt-4 pt-4 border-t border-zinc-700 grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Contact:</span>{' '}
                        <span className="text-zinc-300">{profile.contact_name}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Email:</span>{' '}
                        <span className="text-zinc-300">{profile.contact_email}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Created:</span>{' '}
                        <span className="text-zinc-300">{new Date(profile.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === TABS.LEADS && (
          <div className="space-y-6" data-testid="leads-tab">
            {/* Header with stats */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">Leads Management</h2>
                <p className="text-zinc-400 text-sm">Track and manage incoming leads from campaigns and contact forms</p>
              </div>
              <button
                onClick={downloadLeadsCSV}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                data-testid="download-leads-btn"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <p className="text-zinc-500 text-sm">Total Leads</p>
                <p className="text-2xl font-bold">{leadsStats.total}</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="text-amber-400 text-sm">New</p>
                <p className="text-2xl font-bold text-amber-400">{leadsStats.new}</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-400 text-sm">Contacted</p>
                <p className="text-2xl font-bold text-blue-400">{leadsStats.contacted}</p>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-400 text-sm">Converted</p>
                <p className="text-2xl font-bold text-green-400">{leadsStats.converted}</p>
              </div>
            </div>
            
            {/* Filter */}
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-zinc-500" />
              <select
                value={leadsFilter}
                onChange={(e) => setLeadsFilter(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm"
                data-testid="leads-filter"
              >
                <option value="">All Leads</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={fetchLeads}
                className="text-zinc-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            
            {/* Leads List */}
            {leads.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <UserPlus className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No leads yet</p>
                <p className="text-zinc-500 text-sm mt-1">Leads from contact forms and campaigns will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map(lead => (
                  <div 
                    key={lead.id}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
                    data-testid={`lead-${lead.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">{lead.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            lead.status === 'new' ? 'bg-amber-500/20 text-amber-400' :
                            lead.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                            lead.status === 'qualified' ? 'bg-purple-500/20 text-purple-400' :
                            lead.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {lead.status.toUpperCase()}
                          </span>
                          {lead.source && lead.source !== 'website' && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-[#C41E3A]/20 text-[#C41E3A]">
                              {lead.source}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${lead.email}`} className="hover:text-white">{lead.email}</a>
                          </span>
                          {lead.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${lead.phone}`} className="hover:text-white">{lead.phone}</a>
                            </span>
                          )}
                          {lead.company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {lead.company}
                            </span>
                          )}
                        </div>
                        
                        {lead.message && (
                          <p className="text-zinc-300 text-sm bg-zinc-800/50 p-3 rounded-lg mb-3">
                            "{lead.message}"
                          </p>
                        )}
                        
                        {lead.notes && (
                          <p className="text-zinc-500 text-xs italic">
                            Notes: {lead.notes}
                          </p>
                        )}
                        
                        <p className="text-zinc-500 text-xs mt-2">
                          Received: {new Date(lead.created_at).toLocaleString()}
                          {lead.contacted_at && ` • Contacted: ${new Date(lead.contacted_at).toLocaleString()}`}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {editingLead === lead.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={leadNotes}
                              onChange={(e) => setLeadNotes(e.target.value)}
                              placeholder="Add notes..."
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => updateLeadStatus(lead.id, 'contacted')}
                                disabled={actionLoading}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs"
                              >
                                Contacted
                              </button>
                              <button
                                onClick={() => updateLeadStatus(lead.id, 'converted')}
                                disabled={actionLoading}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs"
                              >
                                Converted
                              </button>
                            </div>
                            <button
                              onClick={() => { setEditingLead(null); setLeadNotes(''); }}
                              className="w-full text-zinc-400 hover:text-white text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingLead(lead.id); setLeadNotes(lead.notes || ''); }}
                            className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg text-sm"
                          >
                            Update Status
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === TABS.USERS && (
          <div className="space-y-6" data-testid="users-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">All Sign-ups</h2>
                <p className="text-zinc-400 text-sm">{usersTotal} registered accounts — including those without profiles</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search by email, name, or company..."
                value={usersSearch}
                onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(0); }}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-zinc-500"
                data-testid="users-search-input"
              />
              <Filter className="w-4 h-4 text-zinc-500 absolute left-3 top-3" />
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
            ) : (
              <>
                <div className="space-y-2">
                  {allUsers.map(u => (
                    <div key={u.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`user-row-${u.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium truncate">{u.email}</span>
                          {u.is_admin && <span className="text-xs bg-[#C41E3A]/20 text-[#C41E3A] px-1.5 py-0.5 rounded">Admin</span>}
                          {u.is_partner && <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Partner</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
                          {u.name && <span>{u.name}</span>}
                          {u.company && <span className="text-zinc-600">• {u.company}</span>}
                          <span className="text-zinc-600">• Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {u.has_company_profile ? (
                            <span className="text-xs bg-green-500/15 text-green-400 border border-green-500/20 px-2 py-0.5 rounded flex items-center gap-1">
                              <Building2 className="w-3 h-3" /> Company Profile
                            </span>
                          ) : (
                            <span className="text-xs bg-zinc-800 text-zinc-500 border border-zinc-700 px-2 py-0.5 rounded">No Company Profile</span>
                          )}
                          {u.has_leadership_profile ? (
                            <span className="text-xs bg-green-500/15 text-green-400 border border-green-500/20 px-2 py-0.5 rounded flex items-center gap-1">
                              <User className="w-3 h-3" /> Leadership Profile
                            </span>
                          ) : (
                            <span className="text-xs bg-zinc-800 text-zinc-500 border border-zinc-700 px-2 py-0.5 rounded">No Leadership Profile</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${u.subscription_tier === 'free' ? 'bg-zinc-800 text-zinc-500' : 'bg-amber-500/15 text-amber-400'}`}>
                            {u.subscription_tier || 'free'}
                          </span>
                        </div>
                      </div>
                      {!u.is_admin && (
                        <button
                          onClick={() => { setUserToDelete(u); setDeleteReason(''); setDeleteNotes(''); setShowDeleteUserModal(true); }}
                          className="text-zinc-600 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-colors shrink-0"
                          title="Delete account"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {allUsers.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">No users found</p>
                  )}
                </div>

                {/* Pagination */}
                {usersTotal > 50 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-500">Showing {usersPage * 50 + 1}–{Math.min((usersPage + 1) * 50, usersTotal)} of {usersTotal}</span>
                    <div className="flex gap-2">
                      <button onClick={() => setUsersPage(Math.max(0, usersPage - 1))} disabled={usersPage === 0} className="px-3 py-1 text-sm bg-zinc-800 rounded hover:bg-zinc-700 disabled:opacity-40 text-zinc-300">Prev</button>
                      <button onClick={() => setUsersPage(usersPage + 1)} disabled={(usersPage + 1) * 50 >= usersTotal} className="px-3 py-1 text-sm bg-zinc-800 rounded hover:bg-zinc-700 disabled:opacity-40 text-zinc-300">Next</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteUserModal && userToDelete && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="delete-user-modal">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-white mb-1">Delete User Account</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Permanently remove <span className="text-white font-medium">{userToDelete.email}</span> and all associated data. The user will be notified via email.
              </p>

              <div className="mb-4">
                <label className="block text-sm text-zinc-400 mb-2">Reason (required)</label>
                <div className="space-y-2">
                  {[
                    { key: 'spam_fake_account', label: 'Spam / Fake Account' },
                    { key: 'community_guidelines_violation', label: 'Community Guidelines Violation' },
                    { key: 'fraudulent_activity', label: 'Fraudulent Activity' },
                    { key: 'duplicate_account', label: 'Duplicate Account' },
                    { key: 'inactivity_no_profile', label: 'Inactivity — No Profile Created' },
                    { key: 'legal_court_order', label: 'Legal / Court Order' },
                  ].map(r => (
                    <label key={r.key} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${deleteReason === r.key ? 'border-[#C41E3A]/50 bg-[#C41E3A]/10' : 'border-zinc-800 hover:border-zinc-700'}`} data-testid={`delete-reason-${r.key}`}>
                      <input
                        type="radio"
                        name="deleteReason"
                        value={r.key}
                        checked={deleteReason === r.key}
                        onChange={() => setDeleteReason(r.key)}
                        className="w-4 h-4 text-[#C41E3A] focus:ring-[#C41E3A]"
                      />
                      <span className="text-sm text-zinc-300">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm text-zinc-400 mb-1">Admin Notes (optional)</label>
                <textarea
                  value={deleteNotes}
                  onChange={(e) => setDeleteNotes(e.target.value)}
                  placeholder="Additional context for the audit trail..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm resize-none h-20"
                  data-testid="delete-notes-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteUserModal(false); setUserToDelete(null); }}
                  className="flex-1 px-4 py-2 border border-zinc-700 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={!deleteReason || actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-delete-user-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Account
                </button>
              </div>

              <p className="text-xs text-zinc-600 mt-3 text-center">
                This action is logged for GDPR/DPDP compliance and retained for 3 years.
              </p>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === TABS.PAYMENTS && (
          <div className="space-y-6" data-testid="payments-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Payment Requests</h2>
                <p className="text-zinc-400 text-sm">Create and manage custom payment links for clients</p>
              </div>
              <button
                onClick={() => setShowCreatePaymentModal(true)}
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg transition-colors"
                data-testid="create-payment-request-btn"
              >
                <Plus className="w-4 h-4" />
                New Payment Request
              </button>
            </div>
            
            {paymentRequests.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <IndianRupee className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No payment requests yet</p>
                <p className="text-zinc-500 text-sm mt-1">Create a payment request to send to a client</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentRequests.map(req => (
                  <div key={req.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid={`payment-request-${req.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-bold text-lg">{req.description}</h3>
                        <p className="text-zinc-400 text-sm flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {req.user_email}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        req.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {req.status.toUpperCase()}
                      </div>
                    </div>
                    
                    {req.line_items && req.line_items.length > 0 && (
                      <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                        <p className="text-xs text-zinc-500 uppercase mb-2">Line Items</p>
                        {req.line_items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm py-1 border-b border-zinc-700/50 last:border-0">
                            <span className="text-zinc-300">{item.description}</span>
                            <span className="text-white">₹{item.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-6 text-sm">
                      <div>
                        <span className="text-zinc-500">INR:</span>
                        <span className="text-white font-bold ml-2">₹{req.amount_inr?.toLocaleString()}</span>
                        <span className="text-zinc-500 ml-1">(+18% GST)</span>
                      </div>
                      {req.amount_usd && (
                        <div>
                          <span className="text-zinc-500">USD:</span>
                          <span className="text-white font-bold ml-2">${req.amount_usd?.toLocaleString()}</span>
                        </div>
                      )}
                      {req.valid_until && (
                        <div className="text-zinc-400">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Expires: {new Date(req.valid_until).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-4 text-xs text-zinc-500">
                      <span>Created: {new Date(req.created_at).toLocaleDateString()}</span>
                      {req.created_by_email && <span>By: {req.created_by_email}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transfers Tab */}
        {activeTab === TABS.TRANSFERS && (
          <div className="space-y-6" data-testid="transfers-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Profile Transfers</h2>
                <p className="text-zinc-400 text-sm">Hand over profile ownership to clients</p>
              </div>
              <button
                onClick={() => setShowTransferModal(true)}
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg transition-colors"
                data-testid="initiate-transfer-btn"
              >
                <Send className="w-4 h-4" />
                Initiate Transfer
              </button>
            </div>
            
            {profileTransfers.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <Send className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No profile transfers yet</p>
                <p className="text-zinc-500 text-sm mt-1">Transfer a profile to give ownership to a client</p>
              </div>
            ) : (
              <div className="space-y-4">
                {profileTransfers.map(transfer => (
                  <div key={transfer.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid={`transfer-${transfer.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                          {transfer.profile_type === 'company' ? (
                            <Building2 className="w-6 h-6 text-zinc-400" />
                          ) : (
                            <User className="w-6 h-6 text-zinc-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{transfer.profile_name}</h3>
                          <p className="text-zinc-400 text-sm capitalize">{transfer.profile_type} Profile</p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        transfer.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        transfer.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {transfer.status.toUpperCase()}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-500">Transfer To</p>
                        <p className="text-white">{transfer.new_owner_email}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Subscription Handling</p>
                        <p className="text-white capitalize flex items-center gap-2">
                          {transfer.subscription_handling === 'keep' && (
                            <><Shield className="w-3 h-3 text-green-400" /> Keep Current</>
                          )}
                          {transfer.subscription_handling === 'coupon' && (
                            <><Gift className="w-3 h-3 text-purple-400" /> Coupon: {transfer.coupon_code}</>
                          )}
                          {transfer.subscription_handling === 'pay' && (
                            <><CreditCard className="w-3 h-3 text-amber-400" /> Requires Payment</>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
                      <div className="flex items-center gap-4">
                        <span>Created: {new Date(transfer.created_at).toLocaleDateString()}</span>
                        <span>Expires: {new Date(transfer.expires_at).toLocaleDateString()}</span>
                      </div>
                      {transfer.status === 'pending' && (
                        <button
                          onClick={() => navigator.clipboard.writeText(`${window.location.origin}/claim-profile/${transfer.token}`)}
                          className="flex items-center gap-1 text-[#C41E3A] hover:text-white transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          Copy Claim Link
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activatingProfile && (
          <ActivateProfileModal
            profile={activatingProfile}
            onClose={() => setActivatingProfile(null)}
            onDone={() => { setActivatingProfile(null); fetchData(); }}
          />
        )}

        {/* Invoices Tab */}
        {activeTab === TABS.INVOICES && (
          <InvoiceManagement />
        )}

        {/* TESTIMONIALS TAB */}
        {activeTab === TABS.TESTIMONIALS && (
          <div>
            <h2 className="text-xl font-bold mb-4">Testimonial Reviews</h2>
            
            {testimonials.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No testimonials submitted yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Pending Review Section */}
                {testimonials.filter(t => t.status === 'pending_review').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-amber-400 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Pending Review ({testimonials.filter(t => t.status === 'pending_review').length})
                    </h3>
                    <div className="space-y-3">
                      {testimonials.filter(t => t.status === 'pending_review').map(t => (
                        <div key={t.id} className="bg-zinc-900/50 border border-amber-500/30 rounded-xl p-4" data-testid={`testimonial-${t.id}`}>
                          <div className="flex items-start gap-4">
                            {/* Video Preview */}
                            <div className="w-48 h-28 bg-black rounded-lg overflow-hidden flex-shrink-0">
                              {t.video_url ? (
                                <video src={t.video_url} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                                  <Video className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            
                            {/* Details */}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-bold">{t.recorder_name}</h4>
                                  <p className="text-sm text-zinc-400">{t.recorder_designation} at {t.recorder_company}</p>
                                </div>
                                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                                  Pending
                                </span>
                              </div>
                              <div className="mt-2 text-sm text-zinc-500">
                                <p>For: <span className="text-white">{t.profile_name}</span> ({t.profile_type} profile)</p>
                                <p>Recorded: {new Date(t.recorded_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-3 mt-4 pt-4 border-t border-zinc-800">
                            {t.video_url && (
                              <a
                                href={t.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg text-sm"
                              >
                                <Play className="w-4 h-4" />
                                Watch Video
                              </a>
                            )}
                            <button
                              onClick={async () => {
                                setActionLoading(true);
                                try {
                                  await axios.post(`${API_URL}/api/admin/testimonials/${t.id}/approve`, {}, { withCredentials: true });
                                  setTestimonials(prev => prev.map(item => 
                                    item.id === t.id ? { ...item, status: 'approved', branding_status: 'processing' } : item
                                  ));
                                } catch (err) {
                                  alert('Failed to approve: ' + (err.response?.data?.detail || err.message));
                                } finally {
                                  setActionLoading(false);
                                }
                              }}
                              disabled={actionLoading}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                              data-testid={`approve-testimonial-${t.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve & Brand
                            </button>
                            <button
                              onClick={async () => {
                                const reason = window.prompt('Rejection reason (optional):');
                                if (reason === null) return;
                                setActionLoading(true);
                                try {
                                  await axios.post(`${API_URL}/api/admin/testimonials/${t.id}/reject?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
                                  setTestimonials(prev => prev.map(item => 
                                    item.id === t.id ? { ...item, status: 'rejected', rejection_reason: reason } : item
                                  ));
                                } catch (err) {
                                  alert('Failed to reject: ' + (err.response?.data?.detail || err.message));
                                } finally {
                                  setActionLoading(false);
                                }
                              }}
                              disabled={actionLoading}
                              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                              data-testid={`reject-testimonial-${t.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Approved Section */}
                {testimonials.filter(t => t.status === 'approved').length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3 text-green-400 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Approved ({testimonials.filter(t => t.status === 'approved').length})
                    </h3>
                    <div className="space-y-3">
                      {testimonials.filter(t => t.status === 'approved').map(t => (
                        <div key={t.id} className="bg-zinc-900/50 border border-green-500/30 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-bold">{t.recorder_name}</h4>
                              <p className="text-sm text-zinc-400">{t.recorder_designation} at {t.recorder_company}</p>
                              <p className="text-xs text-zinc-500 mt-1">For: {t.profile_name}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                t.branding_status === 'complete' ? 'bg-green-500/20 text-green-400' :
                                t.branding_status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                Branding: {t.branding_status || 'pending'}
                              </span>
                              {(t.branding_status === 'processing' || t.branding_status === 'failed') && (
                                <button
                                  onClick={async () => {
                                    try {
                                      setActionLoading(true);
                                      await axios.post(`${API_URL}/api/admin/testimonials/${t.id}/retry-branding`, {}, { withCredentials: true });
                                      alert('Branding retry started!');
                                      // Refresh testimonials
                                      const resp = await axios.get(`${API_URL}/api/admin/testimonials`, { withCredentials: true });
                                      setTestimonials(resp.data.testimonials || []);
                                    } catch (err) {
                                      alert(err.response?.data?.detail || 'Failed to retry branding');
                                    } finally {
                                      setActionLoading(false);
                                    }
                                  }}
                                  disabled={actionLoading}
                                  className="text-xs bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                  data-testid={`retry-branding-${t.id}`}
                                >
                                  Retry Branding
                                </button>
                              )}
                              <span className={`text-xs px-2 py-1 rounded ${t.is_visible ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                {t.is_visible ? 'Visible' : 'Hidden'}
                              </span>
                              {(t.branded_video_url || t.video_url) && (
                                <a
                                  href={t.branded_video_url || t.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#C41E3A] hover:underline text-sm"
                                >
                                  Watch →
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pending Recording Section */}
                {testimonials.filter(t => t.status === 'pending_recording').length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3 text-zinc-400 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Awaiting Recording ({testimonials.filter(t => t.status === 'pending_recording').length})
                    </h3>
                    <div className="space-y-2">
                      {testimonials.filter(t => t.status === 'pending_recording').map(t => (
                        <div key={t.id} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <span className="font-medium">{t.contact_name}</span>
                            <span className="text-zinc-500 mx-2">•</span>
                            <span className="text-zinc-400 text-sm">{t.contact_email}</span>
                            <span className="text-zinc-600 mx-2">→</span>
                            <span className="text-sm">{t.profile_name}</span>
                          </div>
                          <span className="text-xs text-zinc-500">
                            Requested: {new Date(t.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* LEADERSHIP PROFILES TAB */}
        {activeTab === TABS.LEADERSHIP && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Leadership Profiles</h2>
              <Link
                to="/leadership/create"
                className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                data-testid="create-leadership-profile-btn"
              >
                <Plus className="w-4 h-4" />
                Create Leadership Profile
              </Link>
            </div>
            
            {leadershipProfiles.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                <User className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No leadership profiles created yet</p>
                <Link
                  to="/leadership/create"
                  className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Your First Leadership Profile
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {leadershipProfiles.map(profile => (
                  <div key={profile.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid={`leadership-profile-${profile.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      {/* Profile Info */}
                      <div className="flex items-start gap-4 flex-1">
                        {profile.photo_url ? (
                          <img src={profile.photo_url} alt={profile.leader_name} className="w-16 h-16 object-cover rounded-full border-2 border-zinc-700" />
                        ) : (
                          <div className="w-16 h-16 bg-zinc-700 rounded-full flex items-center justify-center">
                            <User className="w-8 h-8 text-zinc-500" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{profile.leader_name}</h3>
                          <p className="text-zinc-400 text-sm">{profile.leader_title}</p>
                          {profile.company_name && (
                            <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                              <Briefcase className="w-3 h-3" />
                              {profile.company_name}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {profile.views || 0} views
                            </span>
                            {profile.created_by && (
                              <span className="bg-[#1E3A5F]/30 text-[#6B9FD4] px-2 py-0.5 rounded text-xs">
                                By: {profile.created_by}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <a
                          href={`/leader/${profile.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                          data-testid={`view-leadership-${profile.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </a>
                        <Link
                          to={`/leadership/edit/${profile.id}`}
                          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                          data-testid={`edit-leadership-${profile.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                          Edit
                        </Link>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete leadership profile for "${profile.leader_name}"? This cannot be undone.`)) return;
                            setActionLoading(true);
                            try {
                              await axios.delete(`${API_URL}/api/leadership-profiles/${profile.id}`, { withCredentials: true });
                              setLeadershipProfiles(prev => prev.filter(p => p.id !== profile.id));
                            } catch (err) {
                              alert('Failed to delete profile: ' + (err.response?.data?.detail || err.message));
                            } finally {
                              setActionLoading(false);
                            }
                          }}
                          disabled={actionLoading}
                          className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                          data-testid={`delete-leadership-${profile.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    {/* Additional Info */}
                    <div className="mt-4 pt-4 border-t border-zinc-700 grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Slug:</span>{' '}
                        <span className="text-zinc-300 font-mono">/leader/{profile.slug}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Contact:</span>{' '}
                        <span className="text-zinc-300">{profile.contact_email || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Created:</span>{' '}
                        <span className="text-zinc-300">{new Date(profile.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Connect (Opportunities) Tab */}
        {activeTab === TABS.CONNECT && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Link2 className="w-6 h-6 text-[#C41E3A]" />
                  BrandStage Connect
                  <span className="bg-amber-500/20 text-amber-400 text-[10px] px-2 py-0.5 rounded-full font-bold ml-1">COMING SOON</span>
                </h2>
                <p className="text-zinc-400 text-sm mt-1">Manage business opportunities and leads — launching soon</p>
              </div>
              <Link
                to="/connect/post"
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Post Opportunity
              </Link>
            </div>
            
            {/* Stats */}
            {connectStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <div className="text-2xl font-bold">{connectStats.total_opportunities}</div>
                  <div className="text-sm text-zinc-400">Total Opportunities</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-amber-400">{connectStats.pending_review}</div>
                  <div className="text-sm text-zinc-400">Pending Review</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-emerald-400">{connectStats.live}</div>
                  <div className="text-sm text-zinc-400">Live</div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-400">{connectStats.total_responses}</div>
                  <div className="text-sm text-zinc-400">Total Responses</div>
                </div>
              </div>
            )}
            
            {/* Filter */}
            <div className="flex gap-2 mb-6">
              {[
                { id: 'all', label: 'All' },
                { id: 'pending_review', label: 'Pending Review' },
                { id: 'live', label: 'Live' },
                { id: 'closed', label: 'Closed' },
                { id: 'rejected', label: 'Rejected' }
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setConnectFilter(filter.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    connectFilter === filter.id
                      ? 'bg-[#C41E3A] text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            
            {/* Opportunities List */}
            {connectOpportunities.length === 0 ? (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-8 text-center">
                <Link2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No opportunities found</p>
                <p className="text-zinc-500 text-sm mt-1">Post the first opportunity to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {connectOpportunities.map(opp => (
                  <div key={opp.id} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{opp.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            opp.status === 'live' ? 'bg-emerald-500/20 text-emerald-400' :
                            opp.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' :
                            opp.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {opp.status.replace('_', ' ').toUpperCase()}
                          </span>
                          {opp.routed_to && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                              Routed to {opp.routed_to.toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-3 text-sm text-zinc-400 mb-3">
                          <span className="bg-zinc-700 px-2 py-0.5 rounded capitalize">{opp.category.replace('_', ' ')}</span>
                          {opp.sector && <span>{opp.sector}</span>}
                          {opp.geography && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {opp.geography}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-zinc-300 text-sm line-clamp-2 mb-3">{opp.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
                          <span>By: {opp.posted_by_name || opp.posted_by_email}</span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {opp.views || 0} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            {opp.response_count || 0} responses
                          </span>
                          <span>{new Date(opp.created_at).toLocaleDateString()}</span>
                        </div>
                        
                        {/* Responses */}
                        {opp.responses && opp.responses.length > 0 && (
                          <div className="mt-4 bg-zinc-900/50 rounded-lg p-3">
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                              <Send className="w-4 h-4 text-[#C41E3A]" />
                              Responses ({opp.responses.length})
                            </h4>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {opp.responses.map((resp, idx) => (
                                <div key={idx} className="text-sm bg-zinc-800 rounded p-2">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{resp.responder_name}</span>
                                    <span className="text-xs text-zinc-500">{new Date(resp.created_at).toLocaleDateString()}</span>
                                  </div>
                                  <div className="text-zinc-400">{resp.responder_company}</div>
                                  <div className="text-zinc-500 text-xs">{resp.responder_email} • {resp.responder_phone}</div>
                                  {resp.message && <p className="text-zinc-300 mt-1 text-xs">{resp.message}</p>}
                                  <span className={`text-xs px-1.5 py-0.5 rounded mt-1 inline-block ${
                                    resp.source === 'referral' ? 'bg-purple-500/20 text-purple-400' :
                                    resp.source === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-zinc-600 text-zinc-300'
                                  }`}>
                                    {resp.source}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {opp.status === 'pending_review' && (
                          <>
                            <button
                              onClick={() => approveOpportunity(opp.id)}
                              disabled={actionLoading}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 disabled:opacity-50"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => rejectOpportunity(opp.id)}
                              disabled={actionLoading}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 disabled:opacity-50"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </>
                        )}
                        {opp.status === 'live' && (
                          <>
                            <button
                              onClick={() => routeOpportunity(opp.id, 'continex')}
                              disabled={actionLoading}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
                            >
                              Route CONTINEX
                            </button>
                            <button
                              onClick={() => routeOpportunity(opp.id, 'tradevesta')}
                              disabled={actionLoading}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
                            >
                              TradeVesta
                            </button>
                            <button
                              onClick={() => closeOpportunity(opp.id)}
                              disabled={actionLoading}
                              className="bg-zinc-600 hover:bg-zinc-500 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
                            >
                              Close
                            </button>
                          </>
                        )}
                        <a
                          href={`/connect?opp=${opp.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#C41E3A] text-sm hover:underline flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Coupons Tab */}
        {activeTab === TABS.COUPONS && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Promotional Coupons</h2>
              <button
                onClick={() => setShowCreateCouponModal(true)}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Coupon
              </button>
            </div>
            
            {coupons.length === 0 ? (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-8 text-center">
                <Gift className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No coupons created yet</p>
                <p className="text-zinc-500 text-sm mt-1">Create promotional codes for profile subscriptions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {coupons.map(coupon => (
                  <div key={coupon.id} className={`bg-zinc-800/50 border rounded-lg p-4 ${coupon.is_active ? 'border-zinc-700' : 'border-red-500/30 opacity-60'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <code className="text-xl font-bold text-white bg-zinc-700 px-3 py-1 rounded">{coupon.code}</code>
                          {coupon.discount_percent === 100 ? (
                            <span className="bg-emerald-500/20 text-emerald-400 text-sm px-2 py-0.5 rounded">100% OFF (FREE)</span>
                          ) : (
                            <span className="bg-amber-500/20 text-amber-400 text-sm px-2 py-0.5 rounded">{coupon.discount_percent}% OFF</span>
                          )}
                          {!coupon.is_active && (
                            <span className="bg-red-500/20 text-red-400 text-sm px-2 py-0.5 rounded">Inactive</span>
                          )}
                        </div>
                        {coupon.description && (
                          <p className="text-zinc-400 text-sm mt-2">{coupon.description}</p>
                        )}
                        <div className="flex gap-4 text-xs text-zinc-500 mt-2">
                          <span>Used: {coupon.used_count || 0}{coupon.max_uses ? `/${coupon.max_uses}` : ' (unlimited)'}</span>
                          {coupon.valid_until && (
                            <span>Expires: {new Date(coupon.valid_until).toLocaleDateString()}</span>
                          )}
                          <span>Created: {coupon.created_at ? new Date(coupon.created_at).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        {coupon.restricted_features && coupon.restricted_features.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <span className="text-xs text-amber-400">Blocked:</span>
                            {coupon.restricted_features.map(f => (
                              <span key={f} className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded">{f.replace('_', ' ')}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleCoupon(coupon.id)}
                          className={`p-2 rounded-lg transition-colors ${coupon.is_active ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}
                          title={coupon.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {coupon.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => deleteCoupon(coupon.id, coupon.code)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Deactivate"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REFERRALS TAB */}
        {activeTab === TABS.REFERRALS && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-white">{referralData.stats?.total_referrals || 0}</p>
                <p className="text-sm text-zinc-400 mt-1">Total Referrals</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-emerald-400">{referralData.stats?.successful || 0}</p>
                <p className="text-sm text-zinc-400 mt-1">Successful</p>
              </div>
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 text-center">
                <p className="text-3xl font-bold text-[#C41E3A]">₹{(referralData.stats?.total_credits_issued || 0).toLocaleString()}</p>
                <p className="text-sm text-zinc-400 mt-1">Credits Issued</p>
              </div>
            </div>

            {/* Referral Rate Settings */}
            {rateForm && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="referral-rate-settings">
                <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Settings className="w-4 h-4 text-[#C41E3A]" />
                    Referral Rate Settings
                  </h3>
                  {!editingRates ? (
                    <button
                      onClick={() => setEditingRates(true)}
                      className="text-[#C41E3A] hover:text-white text-sm font-medium transition-colors"
                      data-testid="edit-rates-btn"
                    >
                      Edit Rates
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingRates(false); setRateForm(referralSettings); }}
                        className="text-zinc-400 hover:text-white text-sm transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          setSavingRates(true);
                          try {
                            const res = await axios.put(`${API_URL}/api/admin/referral-settings`, rateForm, { withCredentials: true });
                            setReferralSettings(res.data.settings);
                            setRateForm(res.data.settings);
                            setEditingRates(false);
                            alert('Referral rates updated successfully');
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to save rates');
                          } finally {
                            setSavingRates(false);
                          }
                        }}
                        disabled={savingRates}
                        className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                        data-testid="save-rates-btn"
                      >
                        {savingRates ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Standard Referral Rates */}
                    <div>
                      <p className="text-zinc-400 text-xs uppercase tracking-wider mb-3 font-medium">Standard Referral Rates</p>
                      <div className="space-y-2">
                        {[
                          { key: 'basic_6m', label: '6-Month Plan' },
                          { key: 'basic_1y', label: '1-Year Plan' },
                          { key: 'all_access_1y', label: 'All-Access Plan' },
                        ].map(item => (
                          <div key={item.key} className="flex items-center justify-between gap-3">
                            <span className="text-zinc-300 text-sm">{item.label}</span>
                            {editingRates ? (
                              <div className="flex items-center gap-1">
                                <span className="text-zinc-500 text-sm">₹</span>
                                <input
                                  type="number"
                                  value={rateForm.standard?.[item.key] || 0}
                                  onChange={e => setRateForm(f => ({ ...f, standard: { ...f.standard, [item.key]: parseInt(e.target.value) || 0 } }))}
                                  className="w-24 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-white text-sm text-right"
                                  data-testid={`rate-standard-${item.key}`}
                                />
                              </div>
                            ) : (
                              <span className="text-white font-mono font-medium text-sm">₹{(rateForm.standard?.[item.key] || 0).toLocaleString()}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Default Partner Rates */}
                    <div>
                      <p className="text-zinc-400 text-xs uppercase tracking-wider mb-3 font-medium">Default Partner Rates</p>
                      <div className="space-y-2">
                        {[
                          { key: 'basic_6m', label: '6-Month Plan' },
                          { key: 'basic_1y', label: '1-Year Plan' },
                          { key: 'all_access_1y', label: 'All-Access Plan' },
                        ].map(item => (
                          <div key={item.key} className="flex items-center justify-between gap-3">
                            <span className="text-zinc-300 text-sm">{item.label}</span>
                            {editingRates ? (
                              <div className="flex items-center gap-1">
                                <span className="text-zinc-500 text-sm">₹</span>
                                <input
                                  type="number"
                                  value={rateForm.partner?.[item.key] || 0}
                                  onChange={e => setRateForm(f => ({ ...f, partner: { ...f.partner, [item.key]: parseInt(e.target.value) || 0 } }))}
                                  className="w-24 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-white text-sm text-right"
                                  data-testid={`rate-partner-${item.key}`}
                                />
                              </div>
                            ) : (
                              <span className="text-amber-400 font-mono font-medium text-sm">₹{(rateForm.partner?.[item.key] || 0).toLocaleString()}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Referee Discount & Cap */}
                  <div className="grid md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-zinc-800">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-zinc-300 text-sm">Referee Welcome Discount</span>
                      {editingRates ? (
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-500 text-sm">₹</span>
                          <input
                            type="number"
                            value={rateForm.referee_discount || 0}
                            onChange={e => setRateForm(f => ({ ...f, referee_discount: parseInt(e.target.value) || 0 }))}
                            className="w-24 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-white text-sm text-right"
                            data-testid="rate-referee-discount"
                          />
                        </div>
                      ) : (
                        <span className="text-emerald-400 font-mono font-medium text-sm">₹{(rateForm.referee_discount || 0).toLocaleString()}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-zinc-300 text-sm">Credit Cap Per Cycle</span>
                      {editingRates ? (
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-500 text-sm">₹</span>
                          <input
                            type="number"
                            value={rateForm.credit_cap || 0}
                            onChange={e => setRateForm(f => ({ ...f, credit_cap: parseInt(e.target.value) || 0 }))}
                            className="w-24 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 text-white text-sm text-right"
                            data-testid="rate-credit-cap"
                          />
                        </div>
                      ) : (
                        <span className="text-white font-mono font-medium text-sm">₹{(rateForm.credit_cap || 0).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Partner Management Section */}
            <div className="bg-zinc-900/50 border border-amber-500/20 rounded-xl overflow-hidden" data-testid="partner-management-section">
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-400" />
                  Partner Consultants
                  <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">{partnersList.length}</span>
                </h3>
              </div>
              <div className="p-4">
                {/* Toggle partner status */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="email"
                    value={partnerToggleEmail}
                    onChange={e => setPartnerToggleEmail(e.target.value)}
                    placeholder="user@example.com — add or remove partner"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm placeholder:text-zinc-500"
                    data-testid="partner-toggle-email-input"
                  />
                  <button
                    onClick={async () => {
                      if (!partnerToggleEmail.trim()) return;
                      try {
                        const res = await axios.post(`${API_URL}/api/admin/users/${encodeURIComponent(partnerToggleEmail.trim())}/toggle-partner`, {}, { withCredentials: true });
                        alert(`Partner status ${res.data.is_partner ? 'enabled' : 'disabled'} for ${res.data.email}`);
                        setPartnerToggleEmail('');
                        // Refresh partners list
                        const pRes = await axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true });
                        setPartnersList(pRes.data.partners || []);
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Failed to toggle partner status');
                      }
                    }}
                    disabled={actionLoading}
                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-amber-500/30 disabled:opacity-50"
                    data-testid="partner-toggle-btn"
                  >
                    Toggle Partner
                  </button>
                </div>

                {/* Partners list */}
                {partnersList.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="partners-table">
                      <thead className="bg-zinc-800/50 text-zinc-400">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Ref Code</th>
                          <th className="px-4 py-2 text-right">Referrals</th>
                          <th className="px-4 py-2 text-right">Credits Earned</th>
                          <th className="px-4 py-2 text-right">Balance</th>
                          <th className="px-4 py-2 text-left">Custom Rates</th>
                          <th className="px-4 py-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {partnersList.map(p => (
                          <tr key={p.email} className="text-zinc-300">
                            <td className="px-4 py-2 text-white font-medium">{p.name || '—'}</td>
                            <td className="px-4 py-2 text-zinc-400">{p.email}</td>
                            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{p.referral_code || '—'}</td>
                            <td className="px-4 py-2 text-right">{p.total_referrals}</td>
                            <td className="px-4 py-2 text-right font-mono text-amber-400">₹{(p.total_credits_earned || 0).toLocaleString()}</td>
                            <td className="px-4 py-2 text-right font-mono text-emerald-400">₹{(p.credit_balance || 0).toLocaleString()}</td>
                            <td className="px-4 py-2">
                              {editingPartnerRates === p.email ? (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {['basic_6m', 'basic_1y', 'all_access_1y'].map(k => (
                                    <div key={k} className="flex items-center gap-0.5">
                                      <span className="text-[10px] text-zinc-500">{k === 'basic_6m' ? '6m' : k === 'basic_1y' ? '1y' : 'AA'}</span>
                                      <input
                                        type="number"
                                        value={partnerRateForm[k] || ''}
                                        onChange={e => setPartnerRateForm(f => ({ ...f, [k]: e.target.value }))}
                                        className="w-16 bg-zinc-700 border border-zinc-600 rounded px-1 py-0.5 text-white text-xs text-right"
                                        placeholder="₹"
                                        data-testid={`partner-rate-${p.email}-${k}`}
                                      />
                                    </div>
                                  ))}
                                  <button
                                    onClick={async () => {
                                      try {
                                        const rates = {};
                                        ['basic_6m', 'basic_1y', 'all_access_1y'].forEach(k => {
                                          if (partnerRateForm[k] !== '') rates[k] = parseInt(partnerRateForm[k]) || 0;
                                        });
                                        await axios.put(`${API_URL}/api/admin/partners/${encodeURIComponent(p.email)}/rates`, rates, { withCredentials: true });
                                        setEditingPartnerRates(null);
                                        const pRes = await axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true });
                                        setPartnersList(pRes.data.partners || []);
                                        alert('Custom rates saved');
                                      } catch (err) {
                                        alert(err.response?.data?.detail || 'Failed to save rates');
                                      }
                                    }}
                                    className="text-emerald-400 text-xs font-medium hover:underline ml-1"
                                  >
                                    Save
                                  </button>
                                  <button onClick={() => setEditingPartnerRates(null)} className="text-zinc-500 text-xs hover:text-white ml-0.5">
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {p.custom_rates ? (
                                    <span className="text-xs text-amber-400 font-mono">
                                      {Object.entries(p.custom_rates).map(([k, v]) => `${k === 'basic_6m' ? '6m' : k === 'basic_1y' ? '1y' : 'AA'}:₹${v}`).join(' ')}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-zinc-500">Default</span>
                                  )}
                                  <button
                                    onClick={() => {
                                      setEditingPartnerRates(p.email);
                                      setPartnerRateForm({
                                        basic_6m: p.custom_rates?.basic_6m ?? '',
                                        basic_1y: p.custom_rates?.basic_1y ?? '',
                                        all_access_1y: p.custom_rates?.all_access_1y ?? '',
                                      });
                                    }}
                                    className="text-[#C41E3A] text-xs hover:underline"
                                    data-testid={`edit-rates-${p.email}`}
                                  >
                                    Set Rates
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remove partner status from ${p.email}?`)) return;
                                  try {
                                    await axios.post(`${API_URL}/api/admin/users/${encodeURIComponent(p.email)}/toggle-partner`, {}, { withCredentials: true });
                                    const pRes = await axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true });
                                    setPartnersList(pRes.data.partners || []);
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Failed');
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 text-xs underline"
                                data-testid={`remove-partner-${p.email}`}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-4">No partners yet. Enter a user's email above to grant partner status.</p>
                )}
              </div>
            </div>

            {/* Referral table */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-800">
                <h3 className="font-bold text-white">Referral History</h3>
              </div>
              {referralData.referrals?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-800/50 text-zinc-400">
                      <tr>
                        <th className="px-4 py-3 text-left">Referrer</th>
                        <th className="px-4 py-3 text-left">Referred User</th>
                        <th className="px-4 py-3 text-left">Plan</th>
                        <th className="px-4 py-3 text-right">Credit</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {referralData.referrals.map(r => (
                        <tr key={r.id} className="text-zinc-300">
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{r.referrer_name || '—'}</p>
                            <p className="text-xs text-zinc-500">{r.referrer_email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-white font-medium">{r.referee_name || '—'}</p>
                            <p className="text-xs text-zinc-500">{r.referee_email}</p>
                          </td>
                          <td className="px-4 py-3">{r.plan?.replace('_', ' ')}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-400">₹{r.credit_amount}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${r.status === 'credited' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-zinc-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-zinc-500">
                  No referrals yet. Users can share their referral link from the Dashboard.
                </div>
              )}
            </div>
          </div>
        )}

        {/* BROADCASTS TAB */}
        {activeTab === TABS.BROADCASTS && (
          <div className="space-y-6">
            {/* Compose Section */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="broadcast-compose-section">
              <div className="p-4 border-b border-zinc-800">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#C41E3A]" />
                  Compose Broadcast
                </h3>
                <p className="text-zinc-500 text-sm mt-1">Send an email + in-app notification to your users</p>
              </div>
              <div className="p-5 space-y-4">
                {/* Audience Selector */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Audience</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'all', label: 'All Users' },
                      { value: 'active', label: 'Active Subscribers' },
                      { value: 'expired', label: 'Expired / Inactive' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setBroadcastForm(f => ({ ...f, audience: opt.value }))}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          broadcastForm.audience === opt.value
                            ? 'bg-[#C41E3A] text-white'
                            : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 border border-zinc-700'
                        }`}
                        data-testid={`broadcast-audience-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Subject</label>
                  <input
                    type="text"
                    value={broadcastForm.subject}
                    onChange={e => setBroadcastForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="e.g. New Feature Announcement"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-zinc-500 focus:border-[#C41E3A] focus:outline-none"
                    data-testid="broadcast-subject-input"
                  />
                </div>

                {/* Message Body */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Message</label>
                  <textarea
                    value={broadcastForm.message}
                    onChange={e => setBroadcastForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Write your message here. Line breaks will be preserved in the email."
                    rows={8}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm placeholder:text-zinc-500 focus:border-[#C41E3A] focus:outline-none resize-y leading-relaxed"
                    data-testid="broadcast-message-input"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => setBroadcastPreview(true)}
                    disabled={!broadcastForm.subject.trim() || !broadcastForm.message.trim()}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="broadcast-preview-btn"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={async () => {
                      if (!broadcastForm.subject.trim() || !broadcastForm.message.trim()) return;
                      if (!window.confirm(`Send this broadcast to ${broadcastForm.audience === 'all' ? 'ALL users' : broadcastForm.audience === 'active' ? 'ACTIVE subscribers' : 'EXPIRED/INACTIVE users'}?`)) return;
                      setBroadcastSending(true);
                      try {
                        const res = await axios.post(`${API_URL}/api/admin/broadcasts`, broadcastForm, { withCredentials: true });
                        alert(`Broadcast queued for ${res.data.recipient_count} recipients. Emails are being sent in the background.`);
                        setBroadcastForm({ subject: '', message: '', audience: 'all' });
                        setBroadcastPreview(false);
                        // Refresh history
                        const hRes = await axios.get(`${API_URL}/api/admin/broadcasts`, { withCredentials: true });
                        setBroadcastsList(hRes.data.broadcasts || []);
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Failed to send broadcast');
                      } finally {
                        setBroadcastSending(false);
                      }
                    }}
                    disabled={broadcastSending || !broadcastForm.subject.trim() || !broadcastForm.message.trim()}
                    className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="broadcast-send-btn"
                  >
                    {broadcastSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {broadcastSending ? 'Sending...' : 'Send Broadcast'}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview Modal */}
            {broadcastPreview && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setBroadcastPreview(false)}>
                <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="broadcast-preview-modal">
                  {/* Email Preview */}
                  <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                    <div style={{ background: '#18181b', padding: '20px', textAlign: 'center' }}>
                      <img src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" alt="BrandStage" style={{ height: '28px' }} />
                    </div>
                    <div style={{ padding: '28px 20px' }}>
                      <h2 style={{ margin: '0 0 14px', color: '#18181b', fontSize: '18px', fontWeight: 700 }}>{broadcastForm.subject}</h2>
                      <div style={{ color: '#3f3f46', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {broadcastForm.message}
                      </div>
                    </div>
                    <div style={{ background: '#f4f4f5', padding: '14px 20px', textAlign: 'center', fontSize: '11px', color: '#71717a' }}>
                      BrandStage Media &middot; A Continex Initiative
                    </div>
                  </div>
                  <div className="p-4 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Audience: <strong>{broadcastForm.audience === 'all' ? 'All Users' : broadcastForm.audience === 'active' ? 'Active Subscribers' : 'Expired / Inactive'}</strong>
                    </span>
                    <button onClick={() => setBroadcastPreview(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                      Close Preview
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Broadcast History */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="broadcast-history-section">
              <div className="p-4 border-b border-zinc-800">
                <h3 className="font-bold text-white">Broadcast History</h3>
              </div>
              {broadcastsList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="broadcast-history-table">
                    <thead className="bg-zinc-800/50 text-zinc-400">
                      <tr>
                        <th className="px-4 py-3 text-left">Subject</th>
                        <th className="px-4 py-3 text-left">Audience</th>
                        <th className="px-4 py-3 text-right">Recipients</th>
                        <th className="px-4 py-3 text-right">Sent</th>
                        <th className="px-4 py-3 text-right">Failed</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {broadcastsList.map(b => (
                        <tr key={b.id} className="text-zinc-300">
                          <td className="px-4 py-3 text-white font-medium max-w-[200px] truncate">{b.subject}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              b.audience === 'all' ? 'bg-blue-500/20 text-blue-400' :
                              b.audience === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {b.audience === 'all' ? 'All' : b.audience === 'active' ? 'Active' : 'Expired'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">{b.recipient_count}</td>
                          <td className="px-4 py-3 text-right text-emerald-400">{b.emails_sent ?? '—'}</td>
                          <td className="px-4 py-3 text-right text-red-400">{b.emails_failed ?? '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              b.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' :
                              b.status === 'sending' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {b.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-zinc-500 text-xs">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-zinc-500">
                  No broadcasts sent yet. Compose your first one above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* OPPORTUNITIES TAB */}
        {activeTab === TABS.OPPORTUNITIES && <AdminOpportunitiesTab />}

        {/* AUDIT & EXPORT TAB */}
        {activeTab === TABS.AUDIT && (
          <div className="space-y-8">
            {/* CSV Export Section */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#C41E3A]" />
                Export Data (CSV)
              </h2>
              <p className="text-zinc-400 mb-6">Download all data for compliance audits, backup, or analysis.</p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <a
                  href={`${API_URL}/api/admin/export/users`}
                  className="flex items-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 transition-colors"
                  data-testid="export-users-btn"
                >
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <div className="font-semibold">Users</div>
                    <div className="text-sm text-zinc-500">All registered accounts</div>
                  </div>
                  <Download className="w-5 h-5 text-zinc-400 ml-auto" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/export/submissions`}
                  className="flex items-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 transition-colors"
                  data-testid="export-submissions-btn"
                >
                  <Video className="w-8 h-8 text-green-400" />
                  <div>
                    <div className="font-semibold">Video Submissions</div>
                    <div className="text-sm text-zinc-500">All recordings</div>
                  </div>
                  <Download className="w-5 h-5 text-zinc-400 ml-auto" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/export/bookings`}
                  className="flex items-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 transition-colors"
                  data-testid="export-bookings-btn"
                >
                  <CreditCard className="w-8 h-8 text-amber-400" />
                  <div>
                    <div className="font-semibold">Bookings</div>
                    <div className="text-sm text-zinc-500">All booth bookings</div>
                  </div>
                  <Download className="w-5 h-5 text-zinc-400 ml-auto" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/export/profiles`}
                  className="flex items-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 transition-colors"
                  data-testid="export-profiles-btn"
                >
                  <Building2 className="w-8 h-8 text-purple-400" />
                  <div>
                    <div className="font-semibold">Company Profiles</div>
                    <div className="text-sm text-zinc-500">All company profiles</div>
                  </div>
                  <Download className="w-5 h-5 text-zinc-400 ml-auto" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/export/leadership-profiles`}
                  className="flex items-center gap-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg p-4 transition-colors"
                  data-testid="export-leadership-btn"
                >
                  <User className="w-8 h-8 text-cyan-400" />
                  <div>
                    <div className="font-semibold">Leadership Profiles</div>
                    <div className="text-sm text-zinc-500">All leader profiles</div>
                  </div>
                  <Download className="w-5 h-5 text-zinc-400 ml-auto" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/export/audit-log`}
                  className="flex items-center gap-3 bg-[#C41E3A]/20 hover:bg-[#C41E3A]/30 border border-[#C41E3A]/50 rounded-lg p-4 transition-colors"
                  data-testid="export-audit-btn"
                >
                  <Shield className="w-8 h-8 text-[#C41E3A]" />
                  <div>
                    <div className="font-semibold">Audit Log</div>
                    <div className="text-sm text-zinc-400">All consents & declarations</div>
                  </div>
                  <Download className="w-5 h-5 text-[#C41E3A] ml-auto" />
                </a>
              </div>
            </div>
            
            {/* Translation Manager */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#C41E3A]" />
                Translation Manager
              </h2>
              <p className="text-zinc-400 mb-6">Edit profile translations and verify quality with DeepL/Gemini.</p>
              
              <a
                href="/admin/translations"
                className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Globe className="w-4 h-4" />
                Open Translation Manager
              </a>
            </div>
            
            {/* Legal Compliance Documents */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#C41E3A]" />
                Legal Compliance Documents
              </h2>
              <p className="text-zinc-400 mb-6">Download compliance summaries for your legal team.</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <a
                  href={`${API_URL}/api/admin/compliance-document`}
                  className="flex items-center gap-3 bg-gradient-to-r from-[#C41E3A]/20 to-[#C41E3A]/10 hover:from-[#C41E3A]/30 hover:to-[#C41E3A]/20 border border-[#C41E3A]/50 rounded-lg p-4 transition-all"
                  data-testid="download-compliance-doc-btn"
                >
                  <FileText className="w-10 h-10 text-[#C41E3A]" />
                  <div className="flex-1">
                    <div className="font-bold text-white">Compliance & Data Protection Summary</div>
                    <div className="text-sm text-zinc-400">Word document (.docx) for legal review</div>
                    <div className="text-xs text-zinc-500 mt-1">Includes: GDPR, DPDP Act, data processing, third-party providers, security measures</div>
                  </div>
                  <Download className="w-6 h-6 text-[#C41E3A]" />
                </a>
                
                <a
                  href={`${API_URL}/api/admin/legal-review-response`}
                  className="flex items-center gap-3 bg-gradient-to-r from-blue-500/20 to-blue-500/10 hover:from-blue-500/30 hover:to-blue-500/20 border border-blue-500/50 rounded-lg p-4 transition-all"
                  data-testid="download-legal-response-btn"
                >
                  <FileText className="w-10 h-10 text-blue-400" />
                  <div className="flex-1">
                    <div className="font-bold text-white">Legal Review Response</div>
                    <div className="text-sm text-zinc-400">Response to internal legal review</div>
                    <div className="text-xs text-zinc-500 mt-1">Summary of actions taken, implementation status, Q&A responses</div>
                  </div>
                  <Download className="w-6 h-6 text-blue-400" />
                </a>
                
                <div className="flex items-center gap-3 bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <Shield className="w-10 h-10 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-semibold text-zinc-300">Public Legal Pages</div>
                    <div className="text-sm text-zinc-500 space-y-1 mt-1">
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="block text-[#C41E3A] hover:underline">→ Terms of Service</a>
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="block text-[#C41E3A] hover:underline">→ Privacy Policy</a>
                      <a href="/compliance" target="_blank" rel="noopener noreferrer" className="block text-[#C41E3A] hover:underline">→ Compliance Summary</a>
                      <a href="/video-guidelines" target="_blank" rel="noopener noreferrer" className="block text-[#C41E3A] hover:underline">→ Video Guidelines</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Internal Security Register - Admin Only */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-amber-400" />
                Internal Security Register
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded ml-2">INTERNAL USE ONLY</span>
              </h2>
              <p className="text-zinc-400 mb-4 text-sm">
                Detailed technical security information for compliance and audit purposes. Not for public disclosure.
              </p>
              
              <div className="space-y-4 text-sm">
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3">Authentication & Access</h3>
                  <ul className="space-y-2 text-zinc-300">
                    <li>• <strong>Password Storage:</strong> Bcrypt with salt (cost factor 12)</li>
                    <li>• <strong>Session Management:</strong> JWT tokens with 7-day expiry, HTTP-only cookies</li>
                    <li>• <strong>Admin MFA:</strong> Planned for Q4 2026 implementation</li>
                    <li>• <strong>Roles:</strong> Admin (full access), User (own profile only)</li>
                    <li>• <strong>Brute Force Protection:</strong> Rate limiting on auth endpoints</li>
                  </ul>
                </div>
                
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3">Infrastructure & Hosting</h3>
                  <ul className="space-y-2 text-zinc-300">
                    <li>• <strong>Application Hosting:</strong> Emergent Cloud (Kubernetes)</li>
                    <li>• <strong>Database:</strong> MongoDB Atlas (multi-region, encrypted at rest)</li>
                    <li>• <strong>Media Storage:</strong> Cloudinary (US/EU regions, CDN delivery)</li>
                    <li>• <strong>Email Service:</strong> Resend (transactional only)</li>
                    <li>• <strong>Payments:</strong> Razorpay (PCI-DSS Level 1 certified)</li>
                  </ul>
                </div>
                
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3">Logging & Monitoring</h3>
                  <ul className="space-y-2 text-zinc-300">
                    <li>• <strong>Application Logs:</strong> All API requests, errors, auth events</li>
                    <li>• <strong>Admin Audit Trail:</strong> Profile changes, user management, settings</li>
                    <li>• <strong>Retention:</strong> 180 days rolling (CERT-In compliant)</li>
                    <li>• <strong>Time Sync:</strong> NTP synchronised servers</li>
                  </ul>
                </div>
                
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3">Third-Party Processors</h3>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-600">
                        <th className="text-left py-2 text-zinc-400">Provider</th>
                        <th className="text-left py-2 text-zinc-400">Purpose</th>
                        <th className="text-left py-2 text-zinc-400">Data Shared</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      <tr className="border-b border-zinc-700"><td className="py-2">MongoDB Atlas</td><td>Database</td><td>All user data</td></tr>
                      <tr className="border-b border-zinc-700"><td className="py-2">Cloudinary</td><td>Media storage</td><td>Videos, images</td></tr>
                      <tr className="border-b border-zinc-700"><td className="py-2">Razorpay</td><td>Payments</td><td>Transaction data</td></tr>
                      <tr className="border-b border-zinc-700"><td className="py-2">Resend</td><td>Email</td><td>Email addresses, names</td></tr>
                      <tr><td className="py-2">OpenAI</td><td>AI content</td><td>Profile text (not training)</td></tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="font-bold text-white mb-3">Incident Response</h3>
                  <ol className="space-y-1 text-zinc-300 list-decimal ml-4">
                    <li>Technical team detection / user report</li>
                    <li>Initial triage and severity assessment</li>
                    <li>Management notification (immediate for P1)</li>
                    <li>Legal assessment</li>
                    <li>CERT-In reporting (within 6 hours if qualifying)</li>
                    <li>Affected user notification (within 72 hours if required)</li>
                  </ol>
                  <p className="text-zinc-400 mt-3 text-xs">POC: operations@brandstage-media.com</p>
                </div>
              </div>
            </div>

            {/* Audit Log Viewer */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#C41E3A]" />
                  Consent & Declaration Log
                </h2>
                <button
                  onClick={async () => {
                    setAuditLoading(true);
                    try {
                      const res = await axios.get(`${API_URL}/api/admin/audit-log`, { withCredentials: true });
                      setAuditLog(res.data.entries || []);
                    } catch (err) {
                      console.error('Failed to load audit log:', err);
                    } finally {
                      setAuditLoading(false);
                    }
                  }}
                  className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  data-testid="load-audit-log-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${auditLoading ? 'animate-spin' : ''}`} />
                  Load Audit Log
                </button>
              </div>
              <p className="text-zinc-400 mb-4">
                Track all user consents, terms acceptances, and video recording declarations for compliance purposes.
              </p>
              
              {auditLog.length === 0 ? (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-8 text-center">
                  <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">Click &quot;Load Audit Log&quot; to view consent records</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Type</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Timestamp</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">Details</th>
                        <th className="text-left py-3 px-4 text-zinc-400 font-medium">IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.slice(0, 100).map((entry, idx) => (
                        <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.type === 'terms_acceptance' ? 'bg-blue-500/20 text-blue-400' :
                              entry.type === 'video_declaration' ? 'bg-green-500/20 text-green-400' :
                              entry.type === 'submission_declaration' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {entry.type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{entry.user_name || 'N/A'}</div>
                            <div className="text-zinc-500 text-xs">{entry.user_email}</div>
                          </td>
                          <td className="py-3 px-4 text-zinc-400">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-zinc-400 max-w-xs truncate" title={entry.details}>
                            {entry.details}
                          </td>
                          <td className="py-3 px-4 text-zinc-500 font-mono text-xs">
                            {entry.ip_address || 'Not recorded'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {auditLog.length > 100 && (
                    <p className="text-center text-zinc-500 text-sm mt-4">
                      Showing 100 of {auditLog.length} entries. Download CSV for full log.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === TABS.SETTINGS && (
          <div className="space-y-8">
            <AddonsManager />
            <PodcastManager />
            {/* Subscription Pricing Section */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-[#C41E3A]" />
                Subscription Pricing
              </h2>
              <p className="text-zinc-400 mb-6">Basic platform prices (AI-generated profile + video hosting). Pro features are sold as add-ons or via the All-Access bundle.</p>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
                {/* 6 Months Pricing */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold">6-Month Plan</h3>
                      <p className="text-sm text-zinc-500">Profile visible for 6 months</p>
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">₹</span>
                    <input
                      type="number"
                      value={pricingSettings.six_months_price}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        six_months_price: parseInt(e.target.value) || 0
                      })}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white text-lg font-semibold focus:border-[#C41E3A] focus:ring-1 focus:ring-[#C41E3A] transition-colors"
                      placeholder="4999"
                      data-testid="pricing-6months-input"
                    />
                  </div>
                </div>

                {/* 1 Year Pricing */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-bold">1-Year Plan</h3>
                      <p className="text-sm text-zinc-500">Best value - Profile visible for 1 year</p>
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">₹</span>
                    <input
                      type="number"
                      value={pricingSettings.one_year_price}
                      onChange={(e) => setPricingSettings({
                        ...pricingSettings,
                        one_year_price: parseInt(e.target.value) || 0
                      })}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white text-lg font-semibold focus:border-[#C41E3A] focus:ring-1 focus:ring-[#C41E3A] transition-colors"
                      placeholder="7999"
                      data-testid="pricing-1year-input"
                    />
                  </div>
                </div>
              </div>

              {/* Savings Preview */}
              <div className="mt-6 max-w-2xl">
                <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-lg p-4">
                  <p className="text-sm text-zinc-400">
                    <span className="text-green-400 font-medium">Customer saves:</span>{' '}
                    ₹{((pricingSettings.six_months_price * 2) - pricingSettings.one_year_price).toLocaleString()} with the 1-Year plan
                    {' '}({Math.round(((pricingSettings.six_months_price * 2 - pricingSettings.one_year_price) / (pricingSettings.six_months_price * 2)) * 100)}% off)
                  </p>
                </div>
              </div>
              
              {/* USD Pricing Section */}
              <div className="mt-8 pt-6 border-t border-zinc-700">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <span className="text-green-400">$</span>
                  International Pricing (USD)
                </h3>
                <p className="text-zinc-500 text-sm mb-4">For international clients. No GST applied.</p>
                
                <div className="grid md:grid-cols-3 gap-4 max-w-3xl">
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                    <p className="text-sm text-zinc-400 mb-2">6-Month Plan (USD)</p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
                      <input
                        type="number"
                        value={pricingSettings.pro_6m_usd || ''}
                        onChange={(e) => setPricingSettings({
                          ...pricingSettings,
                          pro_6m_usd: parseInt(e.target.value) || 0
                        })}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-2 text-white font-semibold focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                        placeholder="60"
                        data-testid="pricing-6months-usd-input"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                    <p className="text-sm text-zinc-400 mb-2">1-Year Plan (USD)</p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
                      <input
                        type="number"
                        value={pricingSettings.pro_1y_usd || ''}
                        onChange={(e) => setPricingSettings({
                          ...pricingSettings,
                          pro_1y_usd: parseInt(e.target.value) || 0
                        })}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-2 text-white font-semibold focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                        placeholder="100"
                        data-testid="pricing-1year-usd-input"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                    <p className="text-sm text-zinc-400 mb-2">Connect Add-On (USD)</p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
                      <input
                        type="number"
                        value={pricingSettings.connect_usd || ''}
                        onChange={(e) => setPricingSettings({
                          ...pricingSettings,
                          connect_usd: parseInt(e.target.value) || 0
                        })}
                        className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-2 text-white font-semibold focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                        placeholder="36"
                        data-testid="pricing-connect-usd-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* All-Access Bundle Pricing */}
              <div className="mt-6 pt-6 border-t border-zinc-700">
                <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-amber-400" />
                  All-Access Bundle (1 Year)
                </h3>
                <p className="text-sm text-zinc-500 mb-4">Platform plus every Pro add-on. Individual add-on prices are managed in Add-on Services above.</p>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">₹</span>
                    <input
                      type="number"
                      value={pricingSettings.all_access_price ?? 15000}
                      onChange={(e) => setPricingSettings({ ...pricingSettings, all_access_price: parseInt(e.target.value) || 0 })}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white text-lg font-semibold focus:border-[#C41E3A]"
                      data-testid="pricing-all-access-input"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
                    <input
                      type="number"
                      value={pricingSettings.all_access_price_usd ?? 180}
                      onChange={(e) => setPricingSettings({ ...pricingSettings, all_access_price_usd: parseInt(e.target.value) || 0 })}
                      className="w-full bg-zinc-900 border border-zinc-600 rounded-lg pl-8 pr-4 py-3 text-white text-lg font-semibold focus:border-[#C41E3A]"
                      data-testid="pricing-all-access-usd-input"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 flex items-center gap-4">
                <button
                  onClick={savePricingSettings}
                  disabled={pricingLoading}
                  className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  data-testid="save-pricing-btn"
                >
                  {pricingLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Pricing
                    </>
                  )}
                </button>
                
                {pricingSaved && (
                  <span className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Pricing saved successfully!
                  </span>
                )}
              </div>

              {/* Last Updated Info */}
              {pricingSettings.updated_at && (
                <div className="mt-4 text-sm text-zinc-500">
                  Last updated: {new Date(pricingSettings.updated_at).toLocaleString()} by {pricingSettings.updated_by}
                </div>
              )}
            </div>

            {/* Future Settings Sections Placeholder */}
            <div className="bg-zinc-900/30 border border-dashed border-zinc-700 rounded-xl p-6 text-center">
              <Settings className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">More settings coming soon...</p>
              <p className="text-zinc-600 text-xs mt-1">Payment gateway, notification preferences, branding options</p>
            </div>
          </div>
        )}
      </div>

      {/* Payment Confirmation Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Confirm Payment</h2>
            <p className="text-zinc-400 mb-4">
              {selectedItem.customer?.company} - {selectedItem.package_type?.replace('_', ' ')}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  placeholder="16999"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Payment Method</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="manual">Manual / Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Reference / Invoice #</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  placeholder="INV-001 or Transaction ID"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleConfirmPayment(selectedItem.id)}
                disabled={actionLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm & Generate Code'}
              </button>
              <button
                onClick={() => setSelectedItem(null)}
                className="px-6 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Removal Modal */}
      {showRemoveProfileModal && profileToRemove && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-2xl w-full my-8">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Remove Profile
            </h2>
            <p className="text-zinc-400 mb-4">
              Removing profile for <span className="text-white font-semibold">{profileToRemove.company_name}</span>
            </p>
            
            {/* Reason Selection */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">Removal Reason</label>
              <select
                value={removalReason}
                onChange={(e) => handleReasonChange(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              >
                {removalReasons.map(reason => (
                  <option key={reason.id} value={reason.id}>
                    {reason.label} - {reason.description}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Internal Notes */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">Internal Notes (not sent to user)</label>
              <input
                type="text"
                value={removalNotes}
                onChange={(e) => setRemovalNotes(e.target.value)}
                placeholder="Optional notes for your records..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              />
            </div>
            
            {/* Message to User */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                Message to User (will be emailed to {profileToRemove.contact_email})
              </label>
              <textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                rows={12}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm font-mono leading-relaxed"
              />
              <p className="text-xs text-zinc-500 mt-1">
                You can edit this message before sending. The user will receive this via email.
              </p>
            </div>
            
            {/* Info Box */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-blue-400 text-sm">
                <strong>Note:</strong> The user&apos;s account will remain active. They can create a new profile anytime at brandstage-media.com/profile/create
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={confirmRemoveProfile}
                disabled={actionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Remove & Send Notification
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowRemoveProfileModal(false);
                  setProfileToRemove(null);
                  setUserMessage('');
                }}
                className="px-6 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Payment Request Modal */}
      {showCreatePaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-lg w-full my-8" data-testid="create-payment-modal">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-[#C41E3A]" />
              Create Payment Request
            </h2>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Client Email *</label>
                <input
                  type="email"
                  value={newPaymentRequest.user_email}
                  onChange={(e) => setNewPaymentRequest({...newPaymentRequest, user_email: e.target.value})}
                  placeholder="client@company.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="payment-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Description *</label>
                <input
                  type="text"
                  value={newPaymentRequest.description}
                  onChange={(e) => setNewPaymentRequest({...newPaymentRequest, description: e.target.value})}
                  placeholder="e.g., Profile subscription + Video editing"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="payment-description-input"
                />
              </div>
              
              {/* Currency Mode Toggle */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Currency Options for Client</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'inr', label: 'INR Only', desc: 'India clients' },
                    { id: 'usd', label: 'USD Only', desc: 'International' },
                    { id: 'both', label: 'Both', desc: 'Let client choose' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setNewPaymentRequest({...newPaymentRequest, currency_mode: opt.id})}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        newPaymentRequest.currency_mode === opt.id
                          ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`currency-mode-${opt.id}`}
                    >
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs text-zinc-500">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className={`grid ${newPaymentRequest.currency_mode === 'both' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                {/* Show INR input when currency_mode is 'inr' or 'both' */}
                {(newPaymentRequest.currency_mode === 'inr' || newPaymentRequest.currency_mode === 'both') && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Amount (INR) *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">₹</span>
                      <input
                        type="number"
                        value={newPaymentRequest.amount_inr}
                        onChange={(e) => setNewPaymentRequest({...newPaymentRequest, amount_inr: e.target.value})}
                        placeholder="50000"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-2 text-white"
                        data-testid="payment-amount-inr-input"
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">+18% GST will be added</p>
                  </div>
                )}
                {/* Show USD input when currency_mode is 'usd' or 'both' */}
                {(newPaymentRequest.currency_mode === 'usd' || newPaymentRequest.currency_mode === 'both') && (
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Amount (USD) {newPaymentRequest.currency_mode === 'usd' ? '*' : ''}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input
                        type="number"
                        value={newPaymentRequest.amount_usd}
                        onChange={(e) => setNewPaymentRequest({...newPaymentRequest, amount_usd: e.target.value})}
                        placeholder="600"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-2 text-white"
                        data-testid="payment-amount-usd-input"
                      />
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">No GST for international</p>
                  </div>
                )}
              </div>
              
              {/* Line Items */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Line Items (optional)</label>
                
                {newPaymentRequest.line_items.length > 0 && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 mb-3 space-y-2">
                    {newPaymentRequest.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-zinc-300">{item.description}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white">₹{item.amount.toLocaleString()}</span>
                          <button
                            type="button"
                            onClick={() => removeLineItem(idx)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newLineItem.description}
                    onChange={(e) => setNewLineItem({...newLineItem, description: e.target.value})}
                    placeholder="Item description"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="number"
                    value={newLineItem.amount}
                    onChange={(e) => setNewLineItem({...newLineItem, amount: e.target.value})}
                    placeholder="Amount"
                    className="w-24 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={addLineItem}
                    disabled={!newLineItem.description || !newLineItem.amount}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 rounded-lg disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Valid Until (optional)</label>
                  <input
                    type="date"
                    value={newPaymentRequest.valid_until}
                    onChange={(e) => setNewPaymentRequest({...newPaymentRequest, valid_until: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Internal Notes</label>
                  <input
                    type="text"
                    value={newPaymentRequest.notes}
                    onChange={(e) => setNewPaymentRequest({...newPaymentRequest, notes: e.target.value})}
                    placeholder="For internal reference"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
              
              {/* Price Summary */}
              {newPaymentRequest.amount_inr && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                  <p className="text-sm text-zinc-400 mb-2">Client will see:</p>
                  {(newPaymentRequest.currency_mode === 'inr' || newPaymentRequest.currency_mode === 'both') && (
                    <p className="text-white">
                      <span className="font-bold">₹{parseInt(newPaymentRequest.amount_inr || 0).toLocaleString()}</span>
                      <span className="text-zinc-400"> + 18% GST = </span>
                      <span className="text-green-400 font-bold">
                        ₹{Math.round(parseInt(newPaymentRequest.amount_inr || 0) * 1.18).toLocaleString()}
                      </span>
                    </p>
                  )}
                  {(newPaymentRequest.currency_mode === 'usd' || newPaymentRequest.currency_mode === 'both') && newPaymentRequest.amount_usd && (
                    <p className="text-white mt-1">
                      <span className="text-green-400 font-bold">${parseInt(newPaymentRequest.amount_usd || 0).toLocaleString()}</span>
                      <span className="text-zinc-400"> (no GST)</span>
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreatePaymentModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createPaymentRequest}
                disabled={actionLoading || !newPaymentRequest.user_email || !newPaymentRequest.description || 
                  ((newPaymentRequest.currency_mode === 'inr' || newPaymentRequest.currency_mode === 'both') && !newPaymentRequest.amount_inr) ||
                  (newPaymentRequest.currency_mode === 'usd' && !newPaymentRequest.amount_usd)}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-payment-request-btn"
              >
                {actionLoading ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Payment Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-lg w-full my-8" data-testid="transfer-modal">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-[#C41E3A]" />
              Transfer Profile Ownership
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Profile Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTransferForm({...transferForm, profile_type: 'company', profile_id: ''})}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      transferForm.profile_type === 'company'
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="transfer-type-company"
                  >
                    <Building2 className="w-4 h-4" />
                    Company Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransferForm({...transferForm, profile_type: 'leadership', profile_id: ''})}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      transferForm.profile_type === 'leadership'
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="transfer-type-leadership"
                  >
                    <User className="w-4 h-4" />
                    Leadership Profile
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Select Profile *</label>
                <select
                  value={transferForm.profile_id}
                  onChange={(e) => setTransferForm({...transferForm, profile_id: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="transfer-profile-select"
                >
                  <option value="">Select a profile...</option>
                  {(transferForm.profile_type === 'company' ? profiles : leadershipProfiles).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.company_name || p.leader_name} {p.subscription_status === 'active' ? '(Active)' : '(Inactive)'}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">New Owner Email *</label>
                <input
                  type="email"
                  value={transferForm.new_owner_email}
                  onChange={(e) => setTransferForm({...transferForm, new_owner_email: e.target.value})}
                  placeholder="newowner@company.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="transfer-email-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Subscription Handling</label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setTransferForm({...transferForm, subscription_handling: 'keep'})}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      transferForm.subscription_handling === 'keep'
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="transfer-handling-keep"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-400" />
                      <span className="font-medium">Keep Current Subscription</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 ml-6">Profile stays active with existing subscription</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTransferForm({...transferForm, subscription_handling: 'coupon'})}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      transferForm.subscription_handling === 'coupon'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="transfer-handling-coupon"
                  >
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-purple-400" />
                      <span className="font-medium">Generate Coupon</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 ml-6">Create a discount coupon for the new owner</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTransferForm({...transferForm, subscription_handling: 'pay'})}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      transferForm.subscription_handling === 'pay'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="transfer-handling-pay"
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-amber-400" />
                      <span className="font-medium">Require Payment</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 ml-6">New owner must pay to activate profile</p>
                  </button>
                </div>
              </div>
              
              {/* Coupon Options */}
              {transferForm.subscription_handling === 'coupon' && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="block text-sm text-purple-400 mb-1">Coupon Code</label>
                    <input
                      type="text"
                      value={transferForm.coupon_code}
                      onChange={(e) => setTransferForm({...transferForm, coupon_code: e.target.value.toUpperCase()})}
                      placeholder="e.g., WELCOME100"
                      className="w-full bg-zinc-800 border border-purple-500/30 rounded-lg px-4 py-2 text-white uppercase"
                      data-testid="transfer-coupon-code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-purple-400 mb-1">Discount</label>
                    <select
                      value={transferForm.coupon_discount}
                      onChange={(e) => setTransferForm({...transferForm, coupon_discount: parseInt(e.target.value)})}
                      className="w-full bg-zinc-800 border border-purple-500/30 rounded-lg px-4 py-2 text-white"
                      data-testid="transfer-coupon-discount"
                    >
                      <option value="100">100% OFF (FREE)</option>
                      <option value="50">50% OFF</option>
                      <option value="25">25% OFF</option>
                    </select>
                  </div>
                </div>
              )}
              
              {/* Currency Mode for Payment */}
              {transferForm.subscription_handling === 'pay' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <label className="block text-sm text-amber-400 mb-2">Payment Currency (shown to client)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTransferForm({...transferForm, currency_mode: 'inr'})}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        transferForm.currency_mode === 'inr'
                          ? 'border-amber-500 bg-amber-500/20'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid="transfer-currency-inr"
                    >
                      <p className="font-medium">₹ INR</p>
                      <p className="text-xs text-zinc-500">India (+ GST)</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTransferForm({...transferForm, currency_mode: 'usd'})}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        transferForm.currency_mode === 'usd'
                          ? 'border-amber-500 bg-amber-500/20'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid="transfer-currency-usd"
                    >
                      <p className="font-medium">$ USD</p>
                      <p className="text-xs text-zinc-500">International</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowTransferModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={initiateTransfer}
                disabled={actionLoading || !transferForm.profile_id || !transferForm.new_owner_email}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-transfer-btn"
              >
                {actionLoading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Initiate Transfer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Coupon Modal */}
      {showCreateCouponModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#C41E3A]" />
              Create Promo Coupon
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Coupon Code *</label>
                <input
                  type="text"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., LAUNCH50, VIP100"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white uppercase"
                  data-testid="coupon-code-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Discount Percentage</label>
                <select
                  value={newCoupon.discount_percent}
                  onChange={(e) => setNewCoupon({...newCoupon, discount_percent: parseInt(e.target.value)})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="coupon-discount-select"
                >
                  <option value="100">100% OFF (FREE)</option>
                  <option value="60">60% OFF</option>
                  <option value="50">50% OFF</option>
                  <option value="40">40% OFF</option>
                  <option value="30">30% OFF</option>
                  <option value="25">25% OFF</option>
                  <option value="20">20% OFF</option>
                  <option value="10">10% OFF</option>
                </select>
                <p className="text-xs text-zinc-500 mt-1">
                  Base pricing: ₹4,999 (6 months) / ₹7,999 (1 year)
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Max Uses (optional)</label>
                  <input
                    type="number"
                    value={newCoupon.max_uses}
                    onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value})}
                    placeholder="Unlimited"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    data-testid="coupon-max-uses-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Expiry Date (optional)</label>
                  <input
                    type="date"
                    value={newCoupon.valid_until}
                    onChange={(e) => setNewCoupon({...newCoupon, valid_until: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    data-testid="coupon-expiry-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={newCoupon.description}
                  onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                  placeholder="e.g., Pilot user, Influencer promo"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                  data-testid="coupon-description-input"
                />
              </div>

              {/* Feature Restrictions (only relevant for 100% coupons) */}
              {newCoupon.discount_percent === 100 && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Restrict Features (optional)</label>
                  <p className="text-xs text-zinc-500 mb-3">Select features to <span className="text-amber-400 font-medium">block</span> for this coupon. Unselected features stay unlocked.</p>
                  <div className="space-y-2 bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                    {availableFeatures.map(f => (
                      <label key={f.key} className="flex items-center gap-3 cursor-pointer hover:bg-zinc-700/30 rounded px-2 py-1.5 transition-colors" data-testid={`restrict-feature-${f.key}`}>
                        <input
                          type="checkbox"
                          checked={newCoupon.restricted_features.includes(f.key)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...newCoupon.restricted_features, f.key]
                              : newCoupon.restricted_features.filter(k => k !== f.key);
                            setNewCoupon({...newCoupon, restricted_features: updated});
                          }}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#C41E3A] focus:ring-[#C41E3A]"
                        />
                        <span className="text-sm text-zinc-300">{f.name}</span>
                      </label>
                    ))}
                  </div>
                  {newCoupon.restricted_features.length > 0 && (
                    <p className="text-xs text-amber-400 mt-2">
                      {newCoupon.restricted_features.length} feature{newCoupon.restricted_features.length > 1 ? 's' : ''} will be blocked for users redeeming this coupon.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateCouponModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createCoupon}
                disabled={actionLoading || !newCoupon.code.trim()}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2 rounded-lg transition-colors disabled:opacity-50"
                data-testid="create-coupon-btn"
              >
                {actionLoading ? 'Creating...' : 'Create Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Branding Modal */}
      {showBrandingModal && brandingSubmission && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-lg w-full" data-testid="branding-modal">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#C41E3A]" />
              Add Branding
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              Add branding to <span className="text-white font-medium">{brandingSubmission.name}&apos;s</span> video
            </p>
            
            <div className="space-y-4">
              {/* Branding Type Selection */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Select Branding Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBrandingType('brandstage')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      brandingType === 'brandstage'
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="branding-type-brandstage"
                  >
                    <div className="font-semibold mb-1">BrandStage Branding</div>
                    <div className="text-xs text-zinc-400">
                      Default intro & outro with BrandStage logo and background music
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setBrandingType('custom')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      brandingType === 'custom'
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid="branding-type-custom"
                  >
                    <div className="font-semibold mb-1">Client&apos;s Custom Branding</div>
                    <div className="text-xs text-zinc-400">
                      Upload client&apos;s own intro and/or outro videos
                    </div>
                  </button>
                </div>
              </div>
              
              {/* Custom Branding File Uploads */}
              {brandingType === 'custom' && (
                <div className="space-y-4 mt-4 p-4 bg-zinc-800/50 rounded-xl">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Custom Intro Video <span className="text-zinc-500">(optional)</span>
                    </label>
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/mov"
                      onChange={(e) => setCustomIntroFile(e.target.files[0])}
                      className="w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-zinc-700 file:text-white hover:file:bg-zinc-600 file:cursor-pointer"
                      data-testid="custom-intro-input"
                    />
                    {customIntroFile && (
                      <p className="text-xs text-green-400 mt-1">✓ {customIntroFile.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">
                      Custom Outro Video <span className="text-zinc-500">(optional)</span>
                    </label>
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/mov"
                      onChange={(e) => setCustomOutroFile(e.target.files[0])}
                      className="w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-zinc-700 file:text-white hover:file:bg-zinc-600 file:cursor-pointer"
                      data-testid="custom-outro-input"
                    />
                    {customOutroFile && (
                      <p className="text-xs text-green-400 mt-1">✓ {customOutroFile.name}</p>
                    )}
                  </div>
                  
                  <p className="text-xs text-zinc-500">
                    Upload at least one file (intro or outro). Supported formats: MP4, WebM, MOV
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowBrandingModal(false);
                  setBrandingSubmission(null);
                  setBrandingType('brandstage');
                  setCustomIntroFile(null);
                  setCustomOutroFile(null);
                }}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2.5 rounded-lg transition-colors"
                data-testid="branding-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitBranding}
                disabled={brandingLoading || (brandingType === 'custom' && !customIntroFile && !customOutroFile)}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="branding-submit-btn"
              >
                {brandingLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Start Branding
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
